/* eslint-disable no-underscore-dangle, no-nested-ternary --
   Trip._id is the booking id surfaced from the backend (Mongo
   convention); the nested ternaries below are short sign-of-amount
   classifications and read more naturally inline than as helper
   functions. This file mirrors the patterns already established in
   src/Pages/Stats/index.jsx — see CLAUDE.md note on the planned DRY-up
   between the two surfaces. */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Badge, Button, Card, Form, OverlayTrigger, Spinner, Table, Tooltip,
} from 'react-bootstrap';
import { Token, Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';

import Error from '../../Components/Error';
import { counterpartyLabel } from '../../Components/CounterpartyPicker';
import { useCounterpartyNames } from '../../Hooks/useCounterpartyNames';
import useUrlViewState from '../../Hooks/useUrlViewState';
import { get } from '../../Services/API';
import thousandSeparator from '../../Utils/numberFormat';
import {
  formatDate, formatTime, toUtcIsoString, DEFAULT_TIMEZONE,
} from '../../Utils/timezone';
import UserService from '../../Services/UserService';
import Permissions from '../../Utils/Permissions';
import { SETTLED_PAYMENT_STATUSES } from '../../Utils/paymentStatus';
import OrderDetailsModal from '../Orders/Components/OrderDetailsModal';
import BulkPaymentModal from '../Payments/Components/BulkPaymentModal';
import CounterpartyLedgerModal from './Components/CounterpartyLedgerModal';
import { operatorTypeOf, OPERATOR_TYPE_SHORT_LABELS } from '../../Utils/operatorType';
import downloadPdf from '../../Utils/downloadPdf';
import { looseIncludes } from '../Portal/portalFormat';

dayjs.extend(utc);
dayjs.extend(tz);

// Order-line kinds attributable to the OPERATOR side of a booking.
// Mirrors the comment near the PARTNER_SIDE_KINDS set in Stats/index.jsx
// — `adjustment` rows that carry an operator_id are operator-side,
// everything else with kind=adjustment is partner-side. The two sides
// are filtered separately on the same Trip object.
// Which operator does this order line belong to?
//
// Order lines on a shared booking carry several operators' costs.
// `metadata.operator_id` names the owner; legacy rows (and cash_from_client,
// which is rarely tagged) fall back to the booking's primary operator.
//
// Without this check every operator-side figure on a multi-operator booking
// included the OTHER operators' amounts. Neoline's July CSV reported
// TOTAL OPERATOR PRICE 5,920,600 against 5,768,350 actually owed — the gap
// being 169,250 belonging to Gógán Enikő on PU_DASH_3977469, a booking the
// two of them worked together. `Net Owed` was right throughout because it
// reads the backend's per-operator `operator_balance_in_settlement`, so the
// two totals on the same report disagreed with nothing to explain why.
function lineBelongsToOperator(line, trip, operatorId) {
  if (!operatorId) return true;
  const owner = line?.metadata?.operator_id || trip?.operator_id;
  return !owner || owner === operatorId;
}

const OPERATOR_SIDE_KINDS = new Set([
  'operator', 'operator_cancellation', 'cash_from_client',
]);

// Tolerances for "is this number effectively zero?" comparisons.
// Mirrors the 0.005 used in the Order Ledger and Stats reconciliation
// panels — half a cent / one Ft of slop.
const ZERO_EPSILON = 0.005;

// Total visible columns in the redesigned trips ledger table. The
// summary row occupies these one-to-one; expanded detail rows reuse
// the same width via colSpan-based indenting (see comments inside
// the helpers below).
//
// Column layout (11 total):
//   1  expand-toggle chevron
//   2  Booking (reference + name + datetime + programme)
//   3  Status
//   4  Date (booking date, no time — for at-a-glance scanning)
//   5  Type (Experience vs Transfer)
//   6  Class (vehicle category)
//   7  Cash (cash collected from client on the booking)
//   8  Gross (pre-cash; same as the value the Amount column shows on
//      detail rows). The previous "Owed" (= Gross − Cash) was dropped
//      because it duplicated Gross whenever cash=0 (the common case)
//      and the Cash + Paid columns already net it down to Still due.
//   9  Paid
//  10  Still due
//  11  Actions (Ledger drill-down)
const LEDGER_COLUMNS = 11;
// Number of "head" columns before the Amount column on detail rows.
// Description spans cols 2..7 (after the chevron). 6 columns.
const LEDGER_HEAD_COLSPAN = 6;
// Number of trailing meta columns to the right of the Amount column on
// detail rows: Paid + Still due + Actions = 3.
const LEDGER_TAIL_COLSPAN = 3;

// The counterparty filter's URL param and its "nothing selected" value. An
// array default is what tells `useUrlViewState` to read the param as JSON.
const FILTER_URL_DEFAULTS = { counterparties: [] };

// Default month picker value — current YYYY-MM in the project's
// reporting timezone (so an admin in the US still sees the Budapest
// month, matching the rest of Stats).
function defaultMonthString() {
  return dayjs().tz(DEFAULT_TIMEZONE).format('YYYY-MM');
}

// Convert a YYYY-MM string into the inclusive [start-of-month,
// end-of-month] window the month-end summary endpoint expects. Returned
// as YYYY-MM-DD strings — the backend handler converts these into a
// full UTC ISO range on its side.
function deriveRangeFromMonth(monthStr) {
  const base = dayjs.tz(`${monthStr}-01`, DEFAULT_TIMEZONE);
  return {
    from: base.startOf('month').format('YYYY-MM-DD'),
    to: base.endOf('month').format('YYYY-MM-DD'),
  };
}

// Counterparty row label colour — red when we still owe / they still
// owe, green when settled, blue when over-paid. Mirrors the
// stillColor logic in Stats/index.jsx.
function stillColorClass(amount) {
  if (amount > ZERO_EPSILON) return 'text-danger';
  if (amount < -ZERO_EPSILON) return 'text-primary';
  return 'text-success';
}

// Profit cell colour — inverse semantic to `stillColorClass`: positive
// profit is good (default text colour), negative profit is bad (red).
// Zero stays neutral. Used on the section-header rollup so the
// visual signal is consistent.
function profitColorClass(amount) {
  if (amount < -ZERO_EPSILON) return 'text-danger';
  return '';
}

// Per-row Profit tone — margin-based, matching the OrdersTable indicator
// thresholds (Pages/Orders/Components/OrdersTable.jsx). Red if the margin
// is < 10% (including negative), yellow 10–20%, green ≥ 20%. We use this
// for the inline Profit cell on counterparty rows so a partner/operator
// with a low margin reads visually distinct from one with a healthy
// margin, the same way it does on individual bookings.
function profitToneClass(marginPct) {
  if (!Number.isFinite(marginPct) || marginPct < 10) return 'text-danger';
  if (marginPct < 20) return 'text-warning';
  return 'text-success';
}

// Margin denominator differs by row side:
//   partner: gross_owed IS the revenue from this partner (we charge it).
//            margin = profit / gross_owed.
//   operator: gross_owed is what we PAY the operator (it's our cost).
//             revenue_share attributable to the operator = profit + cost.
//             margin = profit / (profit + gross_owed).
// Both denominators can be 0 (e.g. a free booking with operator paid),
// in which case margin is undefined → returns null and the caller falls
// back to the "danger" / signed-only treatment.
function profitMarginPct(row) {
  const profit = Number(row?.profit) || 0;
  const gross = Number(row?.gross) || 0;
  const denom = row?.side === 'partner' ? gross : profit + gross;
  if (Math.abs(denom) < ZERO_EPSILON) return null;
  return (profit / denom) * 100;
}

// Sum cash collected from client on a trip. Mirrors the operator-side
// `cash_from_client` orientation where `credit_operator` is negative
// (the operator already pocketed the cash, reducing what we owe).
// We negate so the returned scalar reads as a positive "cash received"
// number. Falls back to the scalar `trip.cash_received` projection if
// no order_lines are available — the trip projection from the backend
// surfaces both, and we pick whichever is non-zero first.
function tripCashReceived(trip) {
  const lines = Array.isArray(trip?.order_lines) ? trip.order_lines : [];
  let fromLines = 0;
  let lineCurrency = null;
  lines.forEach((l) => {
    if (l && l.kind === 'cash_from_client') {
      const amt = Number(l.credit_operator) || 0;
      fromLines += -amt; // negative credit_operator → positive cash
      if (!lineCurrency && l.currency) lineCurrency = l.currency;
    }
  });
  if (Math.abs(fromLines) > ZERO_EPSILON) {
    const cur = lineCurrency
      || trip.cash_received_currency
      || trip.operator_currency
      || trip.currency;
    return { amount: fromLines, currency: cur };
  }
  const scalar = Number(trip?.cash_received) || 0;
  return {
    amount: scalar,
    currency: trip?.cash_received_currency || trip?.operator_currency || trip?.currency,
  };
}

// Render an `<amount> <currency>` string, optionally annotated with the
// FX rate(s) that crossed source-line currencies → display currency.
//
// - `amount`        — number already FX-normalised into `displayCcy`.
// - `displayCcy`    — the currency the amount is expressed in (operator
//                     settlement currency on the operator side; booking
//                     currency on the partner side).
// - `sourceCcys`    — set/array of currencies that contributed to this
//                     amount (typically `trip.source_currencies` from
//                     the backend; for aggregated row totals pass the
//                     union across the row's trips).
// - `fxRates`       — { "SRC->DST": rate } map from the response-level
//                     `fx.rates` field.
//
// When every source currency equals `displayCcy` (single-currency
// booking), the annotation is omitted. When a source currency differs,
// we append `(@ SRC→DST <rate>)` for each crossing pair, separated by
// `;` if more than one. Pairs missing from `fxRates` are dropped
// silently — better than emitting "@ EUR→HUF undefined".
function renderMoney(amount, displayCcy, sourceCcys, fxRates) {
  const amountStr = `${thousandSeparator(amount)} ${displayCcy || ''}`.trim();
  if (!displayCcy || !sourceCcys || !fxRates) return amountStr;
  const sourceList = Array.isArray(sourceCcys)
    ? sourceCcys
    : Array.from(sourceCcys);
  const crossings = sourceList
    .filter((c) => c && c !== displayCcy)
    .filter((c, i, arr) => arr.indexOf(c) === i); // dedup
  if (crossings.length === 0) return amountStr;
  const parts = crossings
    .map((src) => {
      const rate = fxRates[`${src}->${displayCcy}`];
      if (typeof rate !== 'number' || !Number.isFinite(rate)) return null;
      return `${src}→${displayCcy} ${thousandSeparator(rate)}`;
    })
    .filter(Boolean);
  if (parts.length === 0) return amountStr;
  // FX annotation rendered as smaller, muted text — informational
  // (the displayed amount is the main thing; rate is for audit). Uses
  // a thin space between amount and annotation so it doesn't crowd the
  // number on tight columns.
  return (
    <>
      {amountStr}
      {' '}
      <small className="text-muted fw-normal">
        {`(@ ${parts.join('; ')})`}
      </small>
    </>
  );
}

// Render the collapsed summary row for a single booking. Shared by
// the operator- and partner-side helpers — only the owed/paid/stillDue
// numbers differ, so they are passed in pre-computed.
function renderSummaryRow({
  trip,
  owed,
  paid,
  stillDue,
  // Gross = price + cancellation + adjustments BEFORE cash deduction.
  // Distinct from `owed` (which is post-cash). Surfaced as its own
  // column in the sub-report so an admin can see the operator's
  // billable total even when cash already partially settled it.
  gross,
  currency,
  isExpanded,
  onToggle,
  setDrilldownTrip,
  // Flag set when the per-booking ledger encountered payment rows in a
  // currency other than the target settlement/booking currency. Those
  // payments are excluded from the math (we don't have anchor FX rates
  // for payments). Currently unused for layout — future PR will show a
  // small banner — but accepting the prop keeps the call-site contract
  // explicit and lint-clean.
  // eslint-disable-next-line no-unused-vars
  hasMixedCurrencyPayments,
  // FX rates map from the response-level `fx.rates` field. Currently
  // only consumed by per-trip-subtotal rows inside the expanded
  // ledger; the collapsed summary row renders raw (the rate would
  // clutter the at-a-glance view). Kept on the prop list for the
  // subtotal rendering path and so the contract stays explicit.
  // eslint-disable-next-line no-unused-vars
  fxRates,
  // Original (pre-FX) gross amount in the underlying booking/operator
  // currency. When set AND the original currency differs from the
  // row's display `currency`, a small muted secondary line is rendered
  // beneath the Gross number so finance can spot-check the conversion
  // without opening the booking detail. When the two currencies match
  // (no conversion happened), this is left null by the caller and the
  // Gross cell renders exactly as before.
  grossOriginalAmount = null,
  grossOriginalCurrency = null,
}) {
  const stillCls = stillColorClass(stillDue);
  const dueLabel = stillDue > ZERO_EPSILON
    ? 'Still due'
    : stillDue < -ZERO_EPSILON
      ? 'Over'
      : 'Settled';
  const cash = tripCashReceived(trip);
  // Type column content. Three shapes:
  //   - Plain A→B transfer (no programme, no stops): "Transfer A → B"
  //     with the addresses truncated to keep the cell narrow.
  //   - Multi-stop transfer (stops_count > 0, no programme name): a
  //     "Transfer" line plus a small "· N stops · Xh total" detail.
  //   - Experience (programme_name set): the badge plus a truncated
  //     programme name underneath.
  // Event metadata (event_group / event_name) shows as its own
  // small "Event: …" line below the type when present — applies to
  // any of the three shapes.
  const truncate = (s, n = 28) => {
    if (!s) return s;
    const trimmed = String(s).trim();
    return trimmed.length > n ? `${trimmed.slice(0, n - 1)}…` : trimmed;
  };
  const programmeName = trip.program_name && String(trip.program_name).trim();
  const stopsCount = Number(trip.programme_stops_count) || 0;
  const totalHours = Number(trip.programme_total_hours) || 0;
  let typeNode;
  if (programmeName) {
    typeNode = (
      <>
        <Badge bg="info" pill>Experience</Badge>
        <div className="small text-muted mt-1" title={programmeName}>
          {truncate(programmeName)}
        </div>
      </>
    );
  } else if (stopsCount > 0) {
    typeNode = (
      <>
        <div>Transfer</div>
        <div className="small text-muted">
          {`${stopsCount} stop${stopsCount === 1 ? '' : 's'}`}
          {totalHours > 0 ? ` · ${totalHours}h total` : ''}
        </div>
      </>
    );
  } else {
    const fromTxt = truncate(trip.from, 18);
    const toTxt = truncate(trip.to, 18);
    typeNode = (
      <>
        <div>Transfer</div>
        {(fromTxt || toTxt) && (
          <div
            className="small text-muted"
            title={`${trip.from || ''} → ${trip.to || ''}`}
          >
            {`${fromTxt || '?'} → ${toTxt || '?'}`}
          </div>
        )}
      </>
    );
  }
  const eventLabel = [trip.event_group, trip.event_name].filter(Boolean).join(' · ');
  // When a booking is cancelled but still has a non-zero Owed, the
  // residual comes entirely from cancellation fees + adjustments (the
  // backend zeroes out operator_price / base / cash_from_client on
  // cancelled bookings — see the rollup math fix). Surface that
  // explicitly so the user doesn't read the row as "cancelled but the
  // system still thinks we owe money" — it IS a fee, by design.
  const isCancelled = trip.status === 'Cancelled';
  const hasCancellationResidual = isCancelled && Math.abs(owed) > ZERO_EPSILON;
  return (
    <tr
      key={`${trip._id}-summary`}
      className={`month-end-booking-row${isExpanded ? ' month-end-booking-row-expanded' : ''}`}
      onClick={onToggle}
    >
      <td style={{ width: 28 }} className="text-center text-muted">
        <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
      </td>
      <td
        className="fw-semibold"
        style={{
          textDecoration: isCancelled ? 'line-through' : 'none',
        }}
      >
        <span className="me-2">{trip.booking_reference || trip._id}</span>
        <span className="text-muted fw-normal">
          {' · '}
          {trip.name || '—'}
          {' · '}
          {trip.date_time
            ? `${formatDate(trip.date_time)} ${formatTime(trip.date_time)}`
            : '—'}
          {trip.program_name && ` · ${trip.program_name}`}
        </span>
      </td>
      <td>
        <Badge bg={isCancelled ? 'danger' : 'secondary'} pill>
          {trip.status}
        </Badge>
        {hasCancellationResidual && (
          <div className="small text-muted fst-italic mt-1">
            cancellation fee
          </div>
        )}
      </td>
      <td className="text-muted small">
        {trip.date_time ? formatDate(trip.date_time) : '—'}
      </td>
      <td>
        {typeNode}
        {eventLabel && (
          <div className="small text-muted mt-1" title={eventLabel}>
            <i className="fa fa-calendar-day me-1" aria-hidden="true" />
            {truncate(eventLabel, 28)}
          </div>
        )}
      </td>
      <td>{trip.category || '—'}</td>
      {/* Per-booking collapsed-row money cells: plain "amount
          currency", no FX annotation. The rate gets surfaced one
          level deeper inside the expanded ledger's subtotal row. */}
      <td className="text-end font-monospace">
        {Math.abs(cash.amount) > ZERO_EPSILON
          ? `${thousandSeparator(cash.amount)} ${cash.currency || ''}`.trim()
          : '—'}
      </td>
      {/* Gross = price + cancellation + adjustments BEFORE cash. The
          neighbouring Cash + Paid columns reduce it down to Still due,
          so a separate "Owed" (= Gross − Cash) was redundant — same
          number whenever cash=0, and even when cash≠0 the Cash cell
          already shows the deduction explicitly.

          When the booking's underlying gross is in a different currency
          than the row's display `currency` (e.g. a 1,500 EUR booking
          rendered as ~530,000 HUF under a HUF-settling operator), we
          surface the original amount + currency in small muted text on
          a second line so finance can audit the FX conversion at a
          glance. When the two currencies match, no extra line is
          rendered and the cell looks identical to the pre-feature
          version. */}
      <td className="text-end font-monospace">
        {hasCancellationResidual ? (
          <OverlayTrigger
            placement="top"
            overlay={(
              <Tooltip>
                Cancellation fee only — operator_price and cash collected
                are excluded for cancelled bookings.
              </Tooltip>
            )}
          >
            <span style={{ borderBottom: '1px dotted #999', cursor: 'help' }}>
              {gross != null && Math.abs(gross) > ZERO_EPSILON
                ? `${thousandSeparator(gross)} ${currency || ''}`.trim()
                : '—'}
            </span>
          </OverlayTrigger>
        ) : (
          gross != null && Math.abs(gross) > ZERO_EPSILON
            ? `${thousandSeparator(gross)} ${currency || ''}`.trim()
            : '—'
        )}
        {grossOriginalCurrency
          && grossOriginalAmount != null
          && grossOriginalCurrency !== currency
          && Math.abs(grossOriginalAmount) > ZERO_EPSILON && (
          <div className="small text-muted fw-normal">
            {`${thousandSeparator(grossOriginalAmount)} ${grossOriginalCurrency}`}
          </div>
        )}
      </td>
      <td className="text-end font-monospace">
        {`${thousandSeparator(paid)} ${currency || ''}`.trim()}
      </td>
      <td className={`text-end fw-bold font-monospace ${stillCls}`}>
        {`${thousandSeparator(stillDue)} ${currency || ''}`.trim()}
        <div className="small fw-normal">{dueLabel}</div>
      </td>
      <td className="text-end" onClick={(e) => e.stopPropagation()}>
        {trip._id && (
          <Button
            size="sm"
            variant="link"
            className="p-0"
            onClick={() => setDrilldownTrip(trip)}
            title="Open Order Ledger"
          >
            <i className="fa fa-list" />
            {' Ledger'}
          </Button>
        )}
      </td>
    </tr>
  );
}

// Convert a payment amount into the row's display/settlement currency.
// Returns null when the currencies differ and no usable rate exists —
// callers exclude those and surface the omission rather than silently
// scoring them as zero.
//
// Previously any payment whose currency differed from the target was
// dropped outright with no conversion and no warning, so e.g. a 400 EUR
// payment against a HUF-settling operator vanished from Paid and the
// operator looked unpaid.
function convertPaymentAmount(amount, fromCcy, toCcy, fxRates) {
  if (!fromCcy || !toCcy || fromCcy === toCcy) return amount;
  const rate = (fxRates || {})[`${fromCcy}->${toCcy}`];
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return null;
  return amount * rate;
}

// Which counterparty kinds settle each side's ledger.
//
// Operator side: ONLY kind='operator'. A kind='booking' payment is the
// CUSTOMER paying US and says nothing about what we have wired the
// operator. Counting it here also inverted its sign (direction='in'
// → −1), so a customer's 915 EUR card payment surfaced as "Paid −915"
// on the operator's row and inflated Still Due by the same amount
// (PU_DASH_1675814 / Neoline showed 1,615 due on a 700 booking).
// Verified against prod: no kind='booking' payment carries
// lines[].operator_id, so none of them is an operator settlement.
//
// Partner side: kind='booking' DOES settle — an inbound customer
// payment against a booking is exactly the partner-side receipt — so
// that side keeps both kinds.
function paymentSettlesSide(cpKind, side) {
  return side === 'operator'
    ? cpKind === 'operator'
    : (cpKind === 'booking' || cpKind === 'partner');
}

// Match the payments that belong to one trip on one side, returning
// { payment, amount } entries in the payment's own currency. Shared by
// the operator ledger, the partner ledger and the CSV builder so the
// three can never drift apart on which payments count.
//
// FOR DISPLAYING THE PAYMENT ROWS, NOT FOR TOTALLING THEM. `payments` is the
// response's `payments[]`, which the API capped at 50 sorted occurred_at desc
// until 2026-11-02, while the counterparty-level `paid_in` / `paid_out` are
// summed over the whole matched set. Per-booking Paid used to be derived from
// this walk and was therefore 0 for every booking whose payments sorted below
// the cut: October 2026, the house partner had 129 matched payments, so 80 of
// its 165 in-window bookings showed "Paid 0" against a full allocation
// (checked 2026-11-02). The backend now ships the per-booking figure as a
// scalar — `partner_paid_in_booking_currency` /
// `operator_paid_in_settlement`. Use `tripPaidForSide`, which reads it.
//
// The cap is gone, so the rows this walk renders are complete — rendering
// them is what it is FOR, and a row that showed a paid figure with no payment
// beneath it was the second half of the same defect. It still must not be
// TOTALLED: which line settles which counterparty, and the FX into the
// reporting currency, are decided in the backend fold, and a second copy of
// those rules here is what produced the "Paid 0" rows. Pinned in
// MonthEnd.truncatedPayments.test.jsx.
//
// `counterpartyId` scopes operator-side lines. The API returns every
// payment that references one of this operator's bookings, which on a
// multi-operator booking includes the OTHER operators' wires. Matching
// on booking_id alone therefore credited one operator's payment to all
// of them: PU_DASH_5810853 has three operators and a single 144,000 HUF
// wire to one of them, and all three rows showed it as paid. Every
// operator payment line in prod carries operator_id (2192/2192), so
// requiring it here drops nothing legitimate.
function tripPaymentEntries(trip, payments, side, counterpartyId) {
  return (payments || [])
    .map((p) => {
      if (!p || !SETTLED_PAYMENT_STATUSES.has(p.status)) return null;
      const cpKind = p.counterparty?.kind;
      if (!paymentSettlesSide(cpKind, side)) return null;
      const linesForThis = (p.lines || []).filter((l) => {
        if (!l || l.booking_id !== trip._id) return false;
        // Operator-tagged line: it settles only the operator it names.
        if (side === 'operator' && counterpartyId && l.operator_id) {
          return l.operator_id === counterpartyId;
        }
        return true;
      });
      let amt = 0;
      if (linesForThis.length > 0) {
        amt = linesForThis.reduce((s, l) => s + (Number(l.gross_amount) || 0), 0);
      } else if (cpKind === 'booking' && p.counterparty?.id === trip._id) {
        // Unlined booking payment — partner side only, per paymentSettlesSide.
        amt = Number(p.gross_amount) || 0;
      } else {
        return null;
      }
      if (amt === 0) return null;
      return { payment: p, amount: amt };
    })
    .filter(Boolean);
}

// Per-trip paid attributable to a single counterparty side, in that side's
// reporting currency (operator settlement currency / booking currency).
//
// The backend computes it over the FULL matched payment set and ships it as a
// scalar, for the same reason `partner_net_in_booking_currency` exists: the
// attribution and FX rules belong on one side only, and the browser-side walk
// below is a second copy of them (see `tripPaymentEntries`). That walk is
// kept ONLY as the fallback for an API response that predates the scalar — a
// bundle in flight during a deploy.
//
// It used to be wrong for any counterparty with more than 50 payments in the
// window, because `payments[]` was capped there; that cap was removed
// 2026-11-02. It is STILL wrong whenever a counterparty's payments cross
// currencies — it converts with the response's anchor rates and silently
// drops what it has no rate for. Do not promote it back to the primary
// path.
function tripPaidForSide(trip, payments, side, counterpartyId, fxRates) {
  const isOperator = side === 'operator';
  const backendScalar = isOperator
    ? trip.operator_paid_in_settlement
    : trip.partner_paid_in_booking_currency;
  if (backendScalar != null) return Number(backendScalar) || 0;
  const targetCurrency = isOperator
    ? (trip.settlement_currency || trip.operator_currency || trip.currency || 'EUR')
    : (trip.currency || 'EUR');
  let total = 0;
  tripPaymentEntries(trip, payments, side, counterpartyId).forEach(({ payment: p, amount }) => {
    const amt = convertPaymentAmount(amount, p.currency, targetCurrency, fxRates);
    if (amt === null) return;
    // Operator-side: outbound payments = settling direction. Partner-
    // side: inbound payments = settling direction. Matches the sign
    // convention in the inline ledger renderers above.
    const sign = isOperator
      ? (p.direction === 'out' ? 1 : -1)
      : (p.direction === 'in' ? 1 : -1);
    total += sign * amt;
  });
  return total;
}

// Operator-side ledger renderer. Mirrors the partner-side renderer in
// Stats/index.jsx but pulls the operator subset of order_lines + the
// operator subset of payments. Returns { summary, details } so the
// caller can render the collapsed summary row independently of the
// expanded detail rows.
function operatorSideRowsForTrip(
  trip,
  payments,
  isExpanded,
  onToggle,
  setDrilldownTrip,
  fxRates,
  counterpartyId,
) {
  const orderLines = Array.isArray(trip.order_lines) ? trip.order_lines : [];
  // Operator-side accounting uses `credit_operator` — NOT debit/credit.
  // The partner-side helper below uses debit/credit, which is correct
  // for partner-side accounting. Mixing them is the root cause of the
  // pre-existing "operator Owed = 0 EUR" bug.
  //
  // Sign convention from the backend:
  //  - operator / operator_cancellation / adjustment lines targeting
  //    this operator → credit_operator > 0 (we owe the operator)
  //  - cash_from_client → credit_operator < 0 (operator already got
  //    cash from the customer, reducing what we owe)
  //
  // NB: the per-line _net values are NOT FX-converted — they're shown
  // in each line's own currency as an audit trail. The SUBTOTAL must
  // NOT sum these raw values (different currencies don't add up); it
  // reads the backend-computed `operator_balance_in_settlement` scalar
  // instead, which is already FX-normalised into the operator's
  // settlement currency.
  const operatorLines = orderLines
    .map((l) => ({
      ...l,
      _debit: Number(l.debit) || 0,
      _credit: Number(l.credit) || 0,
      _net: Number(l.credit_operator) || 0,
    }))
    // Operator-side: the three OPERATOR_SIDE_KINDS plus any
    // `adjustment` row carrying an operator_id under metadata
    // (operator-side adjustments share the kind=adjustment label with
    // partner-side ones — disambiguated by metadata.operator_id, matching
    // the Order Ledger's convention).
    .filter((l) => {
      if (Math.abs(l._net) <= ZERO_EPSILON) return false;
      // Same ownership rule as the CSV — a co-operator's line must not
      // appear in this operator's ledger.
      if (!lineBelongsToOperator(l, trip, counterpartyId)) return false;
      if (OPERATOR_SIDE_KINDS.has(l.kind || '')) return true;
      if (l.kind === 'adjustment' && l.metadata?.operator_id) return true;
      return false;
    });

  // Target currency for the subtotal — the operator's settlement
  // currency. Falls back to legacy fields when the backend trip
  // projection is missing the new field (e.g. an older API response in
  // flight). The summary cell labels with THIS currency, never EUR by
  // default, since the operator may settle in HUF/USD/etc.
  const targetCurrency = trip.settlement_currency
    || trip.operator_currency
    || trip.currency
    || 'EUR';
  const tripPayments = tripPaymentEntries(trip, payments, 'operator', counterpartyId);

  // Operator-side accounting orientation: the backend pre-computes the
  // FX-normalised owed/cash/net in the operator's settlement currency
  // so the UI doesn't have to sum raw `credit_operator` values across
  // lines that may be in different currencies (the trigger bug:
  // operator_price in HUF − cash_from_client in EUR summed as raw
  // numbers labelled EUR).
  //
  // Payments may also be in different currencies. We only count
  // payments whose `currency` matches the target settlement currency —
  // mixing in payments in other currencies without an anchor FX rate
  // would produce the same kind of bug. Surface a
  // `hasMixedCurrencyPayments` flag so a future UI warning can be hung
  // off it without re-walking the data.
  const operatorOwed = Number(trip.operator_balance_in_settlement) || 0;
  // Pre-cash gross — same FX target as `owed`. Surfaces in its own
  // column so the sub-report shows the billable total before cash
  // already collected is netted out.
  const operatorGross = Number(trip.operator_gross_in_settlement) || 0;
  // Cross-currency payments are FX-converted into the settlement
  // currency rather than dropped. The flag now means "a payment could
  // not be converted because no rate was available" — those are still
  // excluded, but the omission is surfaced instead of silent. It is only
  // reachable on the fallback path: the backend scalar is converted
  // server-side and never drops a payment.
  let hasMixedCurrencyPayments = false;
  const operatorPaid = trip.operator_paid_in_settlement != null
    ? tripPaidForSide(trip, payments, 'operator', counterpartyId, fxRates)
    : tripPayments.reduce((s, e) => {
      const converted = convertPaymentAmount(e.amount, e.payment.currency, targetCurrency, fxRates);
      if (converted === null) {
        hasMixedCurrencyPayments = true;
        return s;
      }
      const sign = e.payment.direction === 'out' ? 1 : -1;
      return s + sign * converted;
    }, 0);
  const operatorStillDue = operatorOwed - operatorPaid;
  const currency = targetCurrency;

  // Original-currency gross for the secondary "verify the FX
  // conversion" line under the Gross cell. The backend trip projection
  // doesn't (yet) surface a dedicated `operator_gross_in_booking_currency`
  // scalar, so we derive it from the operator-side `operator_lines`
  // themselves: sum the gross-contributing kinds (everything except
  // `cash_from_client`, which is the post-cash deduction) in their
  // native currency. We only surface the secondary line when EVERY
  // contributing line shares a single currency — mixing currencies
  // here would defeat the audit purpose (we'd need an FX-resolved
  // total per source currency, which is what the expanded ledger
  // already shows). When the single source currency equals the row's
  // display currency, no conversion happened and renderSummaryRow
  // suppresses the secondary line.
  //
  // TODO(backend): expose `operator_gross_in_booking_currency` (and the
  // matching currency) on the trip projection so this derivation isn't
  // best-effort. Until then, multi-source-currency bookings silently
  // drop the secondary line on the operator side.
  const grossSourceLines = operatorLines.filter(
    (l) => l.kind !== 'cash_from_client',
  );
  const grossSourceCcys = Array.from(new Set(
    grossSourceLines.map((l) => l.currency).filter(Boolean),
  ));
  let operatorGrossOriginalAmount = null;
  let operatorGrossOriginalCurrency = null;
  if (grossSourceCcys.length === 1) {
    [operatorGrossOriginalCurrency] = grossSourceCcys;
    operatorGrossOriginalAmount = grossSourceLines.reduce(
      (s, l) => s + (Number(l._net) || 0),
      0,
    );
  }

  const summary = renderSummaryRow({
    trip,
    owed: operatorOwed,
    gross: operatorGross,
    paid: operatorPaid,
    stillDue: operatorStillDue,
    currency,
    isExpanded,
    onToggle,
    setDrilldownTrip,
    hasMixedCurrencyPayments,
    fxRates,
    grossOriginalAmount: operatorGrossOriginalAmount,
    grossOriginalCurrency: operatorGrossOriginalCurrency,
  });

  // Expanded detail rows. Column layout matches LEDGER_COLUMNS (12):
  //   col 1                     — indent under the chevron (empty)
  //   col 2                     — Type badge
  //   cols 3..LEDGER_HEAD_COLSPAN+1 — Description (spans Status..Cash)
  //   col 8                     — Amount (text-end, lines up under Owed)
  //   cols 9..12                — Meta / reference (Paid+Still+Currency+Actions)
  const details = [];

  operatorLines.forEach((line) => {
    const label = line.description
      || (line.kind === 'operator' ? 'Operator price'
        : line.kind === 'operator_cancellation' ? 'Operator cancellation fee'
          : line.kind === 'cash_from_client' ? 'Cash collected from client'
            : line.kind === 'adjustment' ? 'Adjustment'
              : line.kind);
    details.push(
      <tr key={`${trip._id}-oline-${line._key || `${line.kind}-${line._net}`}`}>
        <td />
        <td>
          <Badge bg="info">Operator Credit</Badge>
        </td>
        <td colSpan={LEDGER_HEAD_COLSPAN - 1}>{label}</td>
        <td className="text-end">
          {`${line._net >= 0 ? '+' : ''}${thousandSeparator(line._net)} ${line.currency || currency}`}
        </td>
        <td colSpan={LEDGER_TAIL_COLSPAN} className="text-muted">
          {line.kind}
        </td>
      </tr>,
    );
  });

  tripPayments.forEach(({ payment, amount }) => {
    // Operator-side display: outbound payments (we paid them) are the
    // settling direction → green. Inbound (rare; refunds from
    // operator) are red.
    const sign = payment.direction === 'out' ? 1 : -1;
    const signedAmount = sign * amount;
    const ref = payment.external_refs?.stripe_charge_id
      || payment.external_refs?.bank_reference
      || payment._id;
    details.push(
      <tr key={`${trip._id}-payment-${payment._id}`}>
        <td />
        <td>
          <Badge bg={payment.direction === 'out' ? 'success' : 'warning'}>
            Operator Payment
          </Badge>
        </td>
        <td colSpan={LEDGER_HEAD_COLSPAN - 1}>
          {payment.direction === 'out' ? 'Payout' : 'Refund in'}
          {' · '}
          {payment.occurred_at
            ? `${formatDate(payment.occurred_at)} ${formatTime(payment.occurred_at)}`
            : '—'}
        </td>
        <td className={`text-end ${signedAmount < 0 ? 'text-danger' : 'text-success'}`}>
          {`${signedAmount >= 0 ? '+' : ''}${thousandSeparator(signedAmount)} ${payment.currency || currency}`}
        </td>
        <td colSpan={LEDGER_TAIL_COLSPAN} className="text-muted">
          <code className="small">{ref}</code>
        </td>
      </tr>,
    );
  });

  const subtotalColor = stillColorClass(operatorStillDue);
  // Source currencies that contributed to operator-side subtotal —
  // distinct currencies seen on operator-side lines. renderMoney uses
  // this to annotate the FX rate that converted them into the
  // operator's settlement currency. Suppresses the annotation when all
  // lines were already in the target currency.
  const subtotalSourceCcys = Array.from(new Set(
    operatorLines.map((l) => l.currency).filter(Boolean),
  ));
  details.push(
    <tr key={`${trip._id}-subtotal`} style={{ borderTop: '2px solid #dee2e6' }}>
      <td />
      <td className="fw-bold">Subtotal</td>
      <td colSpan={LEDGER_HEAD_COLSPAN - 1} className="fw-bold">
        Operator side
        {' · '}
        {operatorStillDue > ZERO_EPSILON
          ? 'Still due'
          : operatorStillDue < -ZERO_EPSILON
            ? 'Over-paid'
            : 'Settled'}
      </td>
      <td className={`text-end fw-bold ${subtotalColor}`}>
        {renderMoney(operatorStillDue, currency, subtotalSourceCcys, fxRates)}
      </td>
      <td colSpan={LEDGER_TAIL_COLSPAN} className="text-muted">
        {`owed ${thousandSeparator(operatorOwed)} ${currency} − paid out ${thousandSeparator(operatorPaid > 0 ? operatorPaid : 0)} ${currency} + refunded in ${thousandSeparator(operatorPaid < 0 ? -operatorPaid : 0)} ${currency}`}
      </td>
    </tr>,
  );

  details.push(
    <tr key={`${trip._id}-spacer`}>
      <td colSpan={LEDGER_COLUMNS} style={{ background: 'transparent', height: 8 }} />
    </tr>,
  );

  return { summary, details };
}

// Partner-side ledger renderer — copy of the renderer currently inline
// in Stats/index.jsx (lines ~2304–2499). Lifted here verbatim so the
// MonthEnd page is self-contained. A follow-up PR will extract this and
// the operator variant into a single shared component (see the spec
// note about not changing Stats yet). Returns { summary, details } —
// see operatorSideRowsForTrip for the layout convention.
function partnerSideRowsForTrip(
  trip,
  payments,
  isExpanded,
  onToggle,
  setDrilldownTrip,
  fxRates,
  counterpartyId,
) {
  const PARTNER_SIDE_KINDS = new Set([
    'base', 'service', 'partner_cancellation', 'adjustment',
  ]);
  const orderLines = Array.isArray(trip.order_lines) ? trip.order_lines : [];
  // Fix 4: cash_from_client lines belong on the partner side via their
  // NEGATIVE `credit_operator` (the operator already pocketed cash from
  // the customer, so the partner's "Owed" should be reduced by it). The
  // operator-side renderer already uses credit_operator natively; the
  // partner-side renderer reads debit/credit, so cash_from_client lines
  // (which carry no debit/credit) were silently dropped — making partner
  // Owed read GROSS-of-cash while operator Owed read NET-of-cash. They
  // must agree so the booking row's "Net" reconciles to operator+partner
  // sides. We surface cash lines with their `credit_operator` sign on
  // partner side too, so summing _net across all lines is correct.
  const partnerLines = orderLines
    .map((l, idx) => {
      const debit = Number(l.debit) || 0;
      const credit = Number(l.credit) || 0;
      const creditOp = Number(l.credit_operator) || 0;
      // cash_from_client uses credit_operator (negative) for partner-side
      // accounting; every other partner-side kind uses (debit − credit).
      const isCashFromClient = l.kind === 'cash_from_client';
      const net = isCashFromClient ? creditOp : (debit - credit);
      return {
        ...l,
        _idx: idx,
        _debit: debit,
        _credit: credit,
        _credit_operator: creditOp,
        _net: net,
      };
    })
    .filter((l) => {
      if (l.kind === 'cash_from_client') {
        return Math.abs(l._net) > ZERO_EPSILON;
      }
      if (!PARTNER_SIDE_KINDS.has(l.kind || '')) return false;
      return l._debit !== 0 || l._credit !== 0;
    });

  const tripPayments = tripPaymentEntries(trip, payments, 'partner', counterpartyId);

  // Target currency for the subtotal — the booking currency. The
  // backend's per-trip `partner_net_in_booking_currency` is already
  // FX-converted into THIS currency, so the subtotal labels match the
  // numbers without us re-summing raw line values across currencies.
  // The per-line detail rows above still display each line's own
  // currency (audit trail).
  const targetCurrency = trip.currency || 'EUR';

  // Owed/cash/net come from backend scalars in booking currency. If the
  // projection is missing (older API response), fall back to summing
  // the per-line `_net` values — buggy for mixed-currency bookings but
  // matches pre-fix behaviour rather than blanking the row.
  const hasBackendScalar = trip.partner_net_in_booking_currency != null
    || trip.partner_owed_in_booking_currency != null;
  const partnerOwed = hasBackendScalar
    ? Number(trip.partner_net_in_booking_currency) || 0
    : partnerLines.reduce((s, l) => s + l._net, 0);
  // Partner gross = pre-cash owed (total_price + partner_cancellation,
  // before cash_from_client is subtracted). Falls back to the same
  // value as `owed` when the gross scalar isn't on the trip projection
  // (older API response) — partner_owed_in_booking_currency is
  // pre-cash by construction in the backend helper.
  const partnerGross = trip.partner_owed_in_booking_currency != null
    ? Number(trip.partner_owed_in_booking_currency) || 0
    : partnerOwed;
  // Mixed-currency payment behaviour: only sum payments in the target
  // currency. Payments in another currency are FX-converted using the
  // response's anchor rates; only those with no available rate stay
  // excluded, and the flag surfaces that omission. Fallback path only —
  // the backend scalar is converted server-side and drops nothing.
  let hasMixedCurrencyPayments = false;
  const partnerPaid = trip.partner_paid_in_booking_currency != null
    ? tripPaidForSide(trip, payments, 'partner', counterpartyId, fxRates)
    : tripPayments.reduce((s, e) => {
      const converted = convertPaymentAmount(e.amount, e.payment.currency, targetCurrency, fxRates);
      if (converted === null) {
        hasMixedCurrencyPayments = true;
        return s;
      }
      const sign = e.payment.direction === 'in' ? 1 : -1;
      return s + sign * converted;
    }, 0);
  const partnerStillDue = partnerOwed - partnerPaid;
  const currency = targetCurrency;

  const summary = renderSummaryRow({
    trip,
    owed: partnerOwed,
    gross: partnerGross,
    paid: partnerPaid,
    stillDue: partnerStillDue,
    currency,
    isExpanded,
    onToggle,
    setDrilldownTrip,
    hasMixedCurrencyPayments,
    fxRates,
  });

  const details = [];

  partnerLines.forEach((line) => {
    const isCashFromClient = line.kind === 'cash_from_client';
    const label = line.description
      || (line.kind === 'base' ? 'Base price'
        : line.kind === 'service' ? 'Service'
          : line.kind === 'partner_cancellation' ? 'Partner cancellation fee'
            : line.kind === 'adjustment' ? 'Adjustment'
              : isCashFromClient ? '− cash collected by operator'
                : line.kind);
    // Stable key. _key (from getOrderLineKey) is shared between
    // operator- and partner-side lists when both sides reference the
    // same order_line — namespace by line index so React doesn't see
    // a duplicate when the same _key surfaces under a different side.
    const detailKey = `${trip._id}-pline-${line._key || `${line.kind}-${line._idx}`}`;
    details.push(
      <tr key={detailKey}>
        <td />
        <td>
          <Badge bg={isCashFromClient ? 'warning' : 'primary'}>
            {isCashFromClient ? 'Cash deducted' : 'Partner Credit'}
          </Badge>
        </td>
        <td colSpan={LEDGER_HEAD_COLSPAN - 1}>{label}</td>
        <td className="text-end">
          {`${line._net >= 0 ? '+' : ''}${thousandSeparator(line._net)} ${line.currency || currency}`}
        </td>
        <td colSpan={LEDGER_TAIL_COLSPAN} className="text-muted">
          {line.kind}
          {!isCashFromClient && line._credit > 0
            ? ` · credited ${thousandSeparator(line._credit)}`
            : ''}
        </td>
      </tr>,
    );
  });

  tripPayments.forEach(({ payment, amount }) => {
    const sign = payment.direction === 'in' ? 1 : -1;
    const signedAmount = sign * amount;
    const ref = payment.external_refs?.stripe_charge_id
      || payment.external_refs?.bank_reference
      || payment._id;
    details.push(
      <tr key={`${trip._id}-payment-${payment._id}`}>
        <td />
        <td>
          <Badge bg={payment.direction === 'in' ? 'success' : 'warning'}>
            Partner Payment
          </Badge>
        </td>
        <td colSpan={LEDGER_HEAD_COLSPAN - 1}>
          {payment.direction === 'in' ? 'Inbound' : 'Refund out'}
          {' · '}
          {payment.occurred_at
            ? `${formatDate(payment.occurred_at)} ${formatTime(payment.occurred_at)}`
            : '—'}
        </td>
        <td className={`text-end ${signedAmount < 0 ? 'text-danger' : 'text-success'}`}>
          {`${signedAmount >= 0 ? '+' : ''}${thousandSeparator(signedAmount)} ${payment.currency || currency}`}
        </td>
        <td colSpan={LEDGER_TAIL_COLSPAN} className="text-muted">
          <code className="small">{ref}</code>
        </td>
      </tr>,
    );
  });

  const subtotalColor = stillColorClass(partnerStillDue);
  // Partner-side source currencies — same idea as operator-side
  // subtotal: surface FX rate only when lines actually crossed
  // currencies into the booking currency.
  const subtotalSourceCcys = Array.from(new Set(
    partnerLines.map((l) => l.currency).filter(Boolean),
  ));
  details.push(
    <tr key={`${trip._id}-subtotal`} style={{ borderTop: '2px solid #dee2e6' }}>
      <td />
      <td className="fw-bold">Subtotal</td>
      <td colSpan={LEDGER_HEAD_COLSPAN - 1} className="fw-bold">
        Partner side
        {' · '}
        {partnerStillDue > ZERO_EPSILON
          ? 'Still due'
          : partnerStillDue < -ZERO_EPSILON
            ? 'Over-collected'
            : 'Settled'}
      </td>
      <td className={`text-end fw-bold ${subtotalColor}`}>
        {renderMoney(partnerStillDue, currency, subtotalSourceCcys, fxRates)}
      </td>
      <td colSpan={LEDGER_TAIL_COLSPAN} className="text-muted">
        {`owed ${thousandSeparator(partnerOwed)} ${currency} − paid in ${thousandSeparator(partnerPaid > 0 ? partnerPaid : 0)} ${currency} + refunded out ${thousandSeparator(partnerPaid < 0 ? -partnerPaid : 0)} ${currency}`}
      </td>
    </tr>,
  );

  details.push(
    <tr key={`${trip._id}-spacer`}>
      <td colSpan={LEDGER_COLUMNS} style={{ background: 'transparent', height: 8 }} />
    </tr>,
  );

  return { summary, details };
}

// -----------------------------------------------------------------
// CSV export helpers — used by the per-row "Download CSV" button.
// The CSV is what the operator/partner receives so they can reconcile
// per-trip against their own books: one row per booking with the
// owed/cash/paid/still-due breakdown in the counterparty's settlement
// (operator) or booking (partner) currency, followed by a totals block.
// -----------------------------------------------------------------

// Standard CSV cell escaping — wrap in double quotes when the value
// contains a comma, newline, or quote; escape internal quotes by
// doubling them. Numbers/booleans stringify as-is. Null / undefined
// render as empty.
function csvCell(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// Lowercase + non-alphanumeric → '-' for use in CSV filenames. Collapse
// runs of '-' and trim leading/trailing so we get a clean slug.
function slugifyName(name) {
  return String(name || 'counterparty')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'counterparty';
}

// Format a money value for the CSV. Operators typically prefer raw
// numbers in CSV so spreadsheet formulas can pick the amounts up
// directly; descriptive labels live in the column headers. Two decimal
// places, no thousands separator.
function csvMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  if (Math.abs(num) < ZERO_EPSILON) return '0.00';
  return num.toFixed(2);
}

// Render a signed money value with an explicit '+' for positives. Used
// for adjustments and cancellation columns so the sign is obvious.
function csvSignedMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || Math.abs(num) < ZERO_EPSILON) return '';
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}`;
}

// Sum the per-line `_credit_operator` values for a single trip,
// filtered to a predicate. Used to surface operator price /
// operator cancellation / operator adjustments separately for the CSV.
function sumOperatorLines(trip, predicate, operatorId) {
  const lines = Array.isArray(trip?.order_lines) ? trip.order_lines : [];
  let total = 0;
  let currency = null;
  lines.forEach((l) => {
    if (!l || !predicate(l)) return;
    if (!lineBelongsToOperator(l, trip, operatorId)) return;
    const amt = Number(l.credit_operator) || 0;
    if (Math.abs(amt) <= ZERO_EPSILON) return;
    total += amt;
    if (!currency && l.currency) currency = l.currency;
  });
  return { amount: total, currency };
}

// Partner-side equivalent: partner lines use (debit − credit) as the
// signed net, with cash_from_client flipping to credit_operator. We
// filter the partner kinds the same way partnerSideRowsForTrip does.
function sumPartnerLines(trip, predicate) {
  const lines = Array.isArray(trip?.order_lines) ? trip.order_lines : [];
  let total = 0;
  let currency = null;
  lines.forEach((l) => {
    if (!l || !predicate(l)) return;
    const debit = Number(l.debit) || 0;
    const credit = Number(l.credit) || 0;
    const net = debit - credit;
    if (Math.abs(net) <= ZERO_EPSILON) return;
    total += net;
    if (!currency && l.currency) currency = l.currency;
  });
  return { amount: total, currency };
}

// The money that came back FROM the counterparty, per currency, in the
// currency it actually moved in.
//
// This is already netted into `tripPaidForSide` — an operator inbound reduces
// Paid. That made the CSV say we wired an operator less than we did, with no
// column explaining the gap: Fekete's report read "Paid 1,607,440" against
// 2,027,440 actually sent, the 420,000 he returned silently subtracted. The
// operator has no way to reconcile that against their own bank.
//
// Reported natively rather than FX-converted, for the same audit reason as
// cash collected: 735 EUR must read as 735 EUR, never as its HUF equivalent.
function tripReturnedByCurrency(trip, payments, side, counterpartyId) {
  const isOperator = side === 'operator';
  const byCurrency = new Map();
  // Backend-computed over the full matched set, like `tripPaidForSide`, and it
  // HAS to come from the same source: Paid is already net of these, so a Paid
  // summed over every payment next to a Returned summed over the capped 50
  // would not reconstruct the gross that left the account — which is the only
  // reason this column exists. The walk below is the pre-scalar fallback.
  const backendList = isOperator
    ? trip.operator_returned_by_currency
    : trip.partner_returned_by_currency;
  if (Array.isArray(backendList)) {
    backendList.forEach(({ amount, currency }) => {
      if (!amount) return;
      const ccy = currency || '';
      byCurrency.set(ccy, (byCurrency.get(ccy) || 0) + amount);
    });
    return byCurrency;
  }
  tripPaymentEntries(trip, payments, side, counterpartyId).forEach(({ payment: p, amount }) => {
    // The non-settling direction is money coming back to us.
    const isReturn = isOperator ? p.direction === 'in' : p.direction === 'out';
    if (!isReturn || !amount) return;
    const ccy = p.currency || '';
    byCurrency.set(ccy, (byCurrency.get(ccy) || 0) + amount);
  });
  return byCurrency;
}

// "735.00 EUR", or "735.00 EUR; 12000.00 HUF" when a booking saw both.
function formatByCurrency(map) {
  if (!map || map.size === 0) return '—';
  return Array.from(map.entries())
    .sort(([a], [b]) => String(a).localeCompare(String(b)))
    .map(([ccy, amt]) => `${csvMoney(amt)} ${ccy}`.trim())
    .join('; ');
}

// Build the CSV text for a single counterparty for the active period.
//
// NO PROFIT COLUMNS. This file is sent to the operator or partner it
// describes, so it must not carry our margin. It previously emitted a
// per-booking `Profit` column and a `TOTAL PROFIT` line whenever the
// exporting admin held CAN_SEE_GLOBAL_STATS — which is precisely the
// person who downloads it to send on. Profit stays visible on screen for
// staff; it just never leaves in the file.
// `trips` should already be the side-filtered list (multi-currency
// partner rows get pre-filtered by row.currency by the caller).
function buildCounterpartyCsv({
  row, trips, payments, fromStr, toStr, fxRates = {},
}) {
  const isOperator = row.side === 'operator';
  const settlementCurrency = row.currency || '';
  // Profit column is appended right before the trailing Currency column
  // (mirrors the on-screen layout where Profit sits beside Net). Only
  // emitted when the viewer holds CAN_SEE_GLOBAL_STATS — matches the
  // Stats CSV behaviour of omitting the column entirely from the file.
  const headerCols = isOperator
    ? [
      'Booking Reference', 'Status', 'Date', 'Passenger Name', 'From', 'To',
      'Class', 'PAX', 'Type',
      'Programme', 'Programme Stops', 'Programme Duration (h)',
      'Event Group', 'Event Name',
      'Operator Price', 'Operator Adjustments', 'Operator Cancellation',
      'Cash Collected', 'Net Owed', 'Paid', 'Cash Returned', 'Still Due',
      'Currency',
    ]
    : [
      'Booking Reference', 'Status', 'Date', 'Passenger Name', 'From', 'To',
      'Class', 'PAX', 'Type',
      'Programme', 'Programme Stops', 'Programme Duration (h)',
      'Event Group', 'Event Name',
      'Partner Price', 'Partner Adjustments', 'Partner Cancellation',
      'Cash Collected', 'Net Owed', 'Paid', 'Cash Returned', 'Still Due',
      'Currency',
    ];

  const lines = [];
  lines.push([csvCell(`Month End Report — ${row.name || ''}`)].join(','));
  lines.push([csvCell(`Period: ${fromStr} to ${toStr}`)].join(','));
  lines.push('');
  lines.push(headerCols.map(csvCell).join(','));

  let totalOwed = 0;
  // Cash is tracked PER CURRENCY, never summed across them. Finance needs
  // to see the exact amount the operator actually took, in the currency
  // they took it in, to reconcile against their own books — a converted
  // figure cannot be checked against a cash envelope. Derived totals
  // (owed / paid / still due) stay in the settlement currency.
  const totalCashByCurrency = new Map();
  const totalReturnedByCurrency = new Map();
  let totalPaid = 0;
  // Sum of the per-row Price cell (operator-side: kind='operator' credit_op;
  // partner-side: kind='base'|'service' debit-credit). Kept in the row's
  // reporting currency because the priceCell renders that way. Displayed as
  // TOTAL OPERATOR PRICE / TOTAL PARTNER PRICE in the footer.
  let totalPrice = 0;
  let totalPriceCurrency = '';

  trips.forEach((trip) => {
    // Same Type taxonomy the UI sub-report uses (kept simple here —
    // the row's From/To columns carry the A→B detail for plain
    // transfers, and Programme Stops / Duration columns below carry
    // the multi-stop detail, so we don't re-encode them in Type).
    const tripType = trip.program_name
      ? 'Experience'
      : (Number(trip.programme_stops_count) || 0) > 0
        ? 'Transfer (multi-stop)'
        : 'Transfer';
    const dateStr = trip.date_time ? formatDate(trip.date_time) : '';
    const cash = tripCashReceived(trip);
    const cashAmount = Math.abs(cash.amount) > ZERO_EPSILON ? cash.amount : 0;
    const cashCurrency = cash.currency || '';

    let priceCell;
    let adjustmentsCell;
    let cancellationCell;
    let netOwed;
    let paid;
    let currency;

    // Cancelled bookings still get rendered in the CSV so partners can
    // see them, but their cash_from_client shouldn't inflate TOTAL CASH
    // RECEIVED — the operator never actually collected it. Same guard
    // will apply below when we accumulate totalCash.
    const isCancelled = String(trip.status || '').toLowerCase() === 'cancelled';

    // Price cell (operator side: kind='operator' credit_op; partner side:
    // kind='base'|'service' debit − credit). Hoisted out of the if/else
    // so the footer total can read it.
    let price = { amount: 0, currency: '' };

    if (isOperator) {
      price = sumOperatorLines(trip, (l) => l.kind === 'operator', row.id);
      const cancellation = sumOperatorLines(trip, (l) => l.kind === 'operator_cancellation', row.id);
      const adjustments = sumOperatorLines(
        trip,
        (l) => l.kind === 'adjustment' && l.metadata?.operator_id,
        row.id,
      );
      priceCell = Math.abs(price.amount) > ZERO_EPSILON
        ? `${csvMoney(price.amount)} ${price.currency || ''}`.trim()
        : '';
      adjustmentsCell = Math.abs(adjustments.amount) > ZERO_EPSILON
        ? `${csvSignedMoney(adjustments.amount)} ${adjustments.currency || ''}`.trim()
        : '—';
      cancellationCell = Math.abs(cancellation.amount) > ZERO_EPSILON
        ? `${csvSignedMoney(cancellation.amount)} ${cancellation.currency || ''}`.trim()
        : '—';
      netOwed = Number(trip.operator_balance_in_settlement) || 0;
      paid = tripPaidForSide(trip, payments, 'operator', row.id, fxRates);
      currency = trip.settlement_currency
        || trip.operator_currency
        || trip.currency
        || settlementCurrency;
    } else {
      price = sumPartnerLines(trip, (l) => l.kind === 'base' || l.kind === 'service');
      const cancellation = sumPartnerLines(trip, (l) => l.kind === 'partner_cancellation');
      const adjustments = sumPartnerLines(
        trip,
        (l) => l.kind === 'adjustment' && !l.metadata?.operator_id,
      );
      priceCell = Math.abs(price.amount) > ZERO_EPSILON
        ? `${csvMoney(price.amount)} ${price.currency || ''}`.trim()
        : '';
      adjustmentsCell = Math.abs(adjustments.amount) > ZERO_EPSILON
        ? `${csvSignedMoney(adjustments.amount)} ${adjustments.currency || ''}`.trim()
        : '—';
      cancellationCell = Math.abs(cancellation.amount) > ZERO_EPSILON
        ? `${csvSignedMoney(cancellation.amount)} ${cancellation.currency || ''}`.trim()
        : '—';
      netOwed = trip.partner_net_in_booking_currency != null
        ? Number(trip.partner_net_in_booking_currency) || 0
        : Number(trip.partner_owed_in_booking_currency) || 0;
      paid = tripPaidForSide(trip, payments, 'partner', row.id, fxRates);
      currency = trip.currency || settlementCurrency;
    }

    const stillDue = netOwed - paid;
    // Already inside `paid` as a negative; surfaced separately so the reader
    // can reconstruct the gross that actually left the account.
    const returnedSide = isOperator ? 'operator' : 'partner';
    const returnedByCcy = tripReturnedByCurrency(trip, payments, returnedSide, row.id);
    returnedByCcy.forEach((amt, ccy) => {
      totalReturnedByCurrency.set(ccy, (totalReturnedByCurrency.get(ccy) || 0) + amt);
    });
    totalOwed += netOwed;
    // Skip cancelled — the operator never collected cash on a cancelled
    // booking, so summing it into TOTAL CASH RECEIVED overstates what
    // the operator actually holds against us. (Reported by finance
    // via PU_DASH_5774419 — a cancelled booking with a 46 EUR
    // operator_cash_from_client flat field.)
    if (!isCancelled && Math.abs(cashAmount) > ZERO_EPSILON) {
      const cashCcy = cashCurrency || settlementCurrency || '';
      totalCashByCurrency.set(cashCcy, (totalCashByCurrency.get(cashCcy) || 0) + cashAmount);
    }
    totalPaid += paid;
    // Accumulate the per-row Price cell for the footer total. The
    // priceCell was computed above from operator-side operator lines
    // (isOperator) or partner-side base/service lines. In multi-currency
    // rows we still push the last non-empty currency into the label —
    // that matches how the on-screen row labels its currency.
    if (Math.abs(price.amount) > ZERO_EPSILON) {
      totalPrice += price.amount;
      totalPriceCurrency = price.currency || totalPriceCurrency;
    }

    const cashCell = Math.abs(cashAmount) > ZERO_EPSILON
      ? `${csvMoney(cashAmount)} ${cashCurrency}`.trim()
      : '—';

    const programmeStops = Number(trip.programme_stops_count) || 0;
    const programmeHours = Number(trip.programme_total_hours) || 0;
    lines.push([
      csvCell(trip.booking_reference || trip._id || ''),
      csvCell(trip.status || ''),
      csvCell(dateStr),
      csvCell(trip.name || ''),
      csvCell(trip.from || ''),
      csvCell(trip.to || ''),
      csvCell(trip.category || ''),
      csvCell(trip.pax ?? ''),
      csvCell(tripType),
      csvCell(trip.program_name || ''),
      csvCell(programmeStops > 0 ? programmeStops : ''),
      csvCell(programmeHours > 0 ? programmeHours : ''),
      csvCell(trip.event_group || ''),
      csvCell(trip.event_name || ''),
      csvCell(priceCell),
      csvCell(adjustmentsCell),
      csvCell(cancellationCell),
      csvCell(cashCell),
      csvCell(csvMoney(netOwed)),
      csvCell(csvMoney(paid)),
      csvCell(formatByCurrency(returnedByCcy)),
      csvCell(csvMoney(stillDue)),
      csvCell(currency),
    ].join(','));
  });

  // Trailing totals block — two blank lines, then labelled totals so
  // the partner can spot-check against their own ledger total.
  const totalStillDue = totalOwed - totalPaid;
  lines.push('');
  lines.push('');
  // TOTAL {OPERATOR|PARTNER} PRICE — sum of the Price column, in the row's
  // native currency (which is the row's currency label as picked up from
  // the last non-empty priceCell). Labelled per side so multi-side CSVs
  // stay unambiguous.
  const totalPriceLabel = isOperator ? 'TOTAL OPERATOR PRICE' : 'TOTAL PARTNER PRICE';
  lines.push([totalPriceLabel, csvMoney(totalPrice), csvCell(totalPriceCurrency || settlementCurrency)].join(','));
  lines.push(['TOTAL OWED', csvMoney(totalOwed), csvCell(settlementCurrency)].join(','));
  // One row per currency actually received, so 735 EUR reads as "735 EUR"
  // and never as "735 HUF". Previously every currency was summed raw and
  // stamped with the settlement currency, understating a EUR cash total on
  // a HUF-settling operator by the FX factor (~364x).
  if (totalCashByCurrency.size === 0) {
    lines.push(['TOTAL CASH RECEIVED', csvMoney(0), csvCell(settlementCurrency)].join(','));
  } else {
    Array.from(totalCashByCurrency.entries())
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .forEach(([ccy, amount]) => {
        lines.push(['TOTAL CASH RECEIVED', csvMoney(amount), csvCell(ccy)].join(','));
      });
  }
  lines.push(['TOTAL PAID', csvMoney(totalPaid), csvCell(settlementCurrency)].join(','));
  // Per currency, never summed across them — same rule as cash received.
  Array.from(totalReturnedByCurrency.entries())
    .sort(([a], [b]) => String(a).localeCompare(String(b)))
    .forEach(([ccy, amount]) => {
      lines.push(['TOTAL CASH RETURNED', csvMoney(amount), csvCell(ccy)].join(','));
    });
  lines.push(['TOTAL STILL DUE', csvMoney(totalStillDue), csvCell(settlementCurrency)].join(','));

  return lines.join('\n');
}

// Trigger a browser download for the given CSV text using the standard
// Blob + anchor-link pattern. Kept side-effecting on document so the
// caller is a plain function — easier to wire into an onClick handler.
//
// We prepend a UTF-8 BOM (U+FEFF) so Excel-for-Mac decodes the file
// as UTF-8 instead of Mac Roman / Windows-1252. Without the BOM,
// Hungarian characters (Székesfehérvár) and the em-dash placeholder
// render as garbage (Sz√©kesfeh, ‚Äî). macOS Numbers / built-in CSV
// viewer / Sheets / Python autodetect UTF-8 with or without it, so
// the BOM is safe across readers.
function triggerCsvDownload(filename, csvText) {
  const blob = new Blob(['\uFEFF', csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari/Firefox have time to grab the blob before
  // the URL is torn down. setTimeout(0) is the conventional pattern.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// -----------------------------------------------------------------
// Counterparty row data model
// -----------------------------------------------------------------
// `summary` is the raw {operator|partner} row returned by the
// month_end_summary endpoint. We normalise both sides into a single
// shape so the same row renderer works for either.
//
// Field semantics (rationale):
//   gross     — what we accrued. Operator: gross_owed (we owe op);
//               Partner: gross_owed (partner owes us pre-cash).
//   net       — net to settle. Operator: gross − cash (cash already
//               in op's pocket reduces our liability). Partner:
//               summary.net_owed (already net of cash).
//   cash      — cash leg (operator side: cash_collected; partner:
//               cash_collected on the partner row).
//   paid      — paid_out for operators, paid_in for partners.
//   stillDue  — still_to_pay / still_due (the residual to settle).
//
// The renderer applies a leading "−" to the cash column to make it
// read as a deduction in the row math (gross − cash = net).
function normaliseOperatorRow(op) {
  const gross = Number(op.gross_owed) || 0;
  const cash = Number(op.cash_collected) || 0;
  return {
    // `side` stays 'operator' for an expense account, and that is deliberate.
    // It is not a label — it routes the detail fetch, the CSV builder, the
    // ledger modal's `kind` and the bulk payment's default direction, all of
    // which are correct for a cost bucket exactly as they are for a supplier.
    // Only the HEADING a row renders under changes; see `operatorType`.
    side: 'operator',
    // Which of the three sections this row belongs under. Presentation only:
    // no figure on this row reads it. A row from an API that predates the
    // field, or a record nobody has classified, reads as 'operator' — see
    // Utils/operatorType.
    operatorType: operatorTypeOf(op),
    id: op.operator_id,
    // `fetchId` mirrors `id` on the operator side — there's only one
    // settlement currency per operator. Kept symmetric with the partner
    // shape so call sites can ignore the distinction.
    fetchId: op.operator_id,
    name: op.name,
    currency: op.settlement_currency,
    multiCurrency: false,
    gross,
    net: gross - cash,
    cash,
    paid: Number(op.paid_out) || 0,
    stillDue: Number(op.still_to_pay) || 0,
    // Profit in settlement_currency. May be absent on older API
    // responses — default to 0 so the column renders harmlessly while
    // the permission gate hides it from non-admins.
    profit: Number(op.profit) || 0,
    amountOfBookings: Number(op.amount_of_bookings) || 0,
    // Source-line currencies that contributed to this row's totals.
    // The renderer uses these together with `summary.fx.rates` to
    // annotate cells whose display currency disagrees with at least
    // one source-line currency. Defaults to empty array on older API
    // responses that don't surface the field.
    sourceCurrencies: Array.isArray(op.source_currencies) ? op.source_currencies : [],
  };
}

// Backend (Fix 2) emits one row per (partner_id, currency). We compose
// a stable per-row React key as `${partner_id}-${currency}` so two
// rows for the same partner don't collide. `fetchId` is the bare
// partner_id used for the detail fetch — multi-currency rows for the
// same partner share the same booking-detail load. `multiCurrency`
// drives the "(EUR)" parenthetical after the name on the row, so
// single-currency partners stay labelled by name only.
//
// The row id is also what the counterparty filter keeps in the URL, and
// `DirectoryName` splits it back at its LAST hyphen to find the partner —
// change its shape and a shared link stops resolving its partner chips.
function normalisePartnerRow(p, multiCurrency) {
  const gross = Number(p.gross_owed) || 0;
  const cash = Number(p.cash_collected) || 0;
  const currency = p.currency || '';
  return {
    side: 'partner',
    id: `${p.partner_id}-${currency}`,
    fetchId: p.partner_id,
    name: p.name,
    currency,
    multiCurrency,
    gross,
    net: Number(p.net_owed) || (gross - cash),
    cash,
    paid: Number(p.paid_in) || 0,
    stillDue: Number(p.still_due) || 0,
    // Profit in the row's reporting currency. See operator-side note;
    // optional on the wire for backwards compatibility.
    profit: Number(p.profit) || 0,
    amountOfBookings: Number(p.amount_of_bookings) || 0,
    sourceCurrencies: Array.isArray(p.source_currencies) ? p.source_currencies : [],
  };
}

/**
 * What a row is called in the counterparty filter: its badge in the menu, and
 * part of the text the filter searches.
 *
 * It names the SECTION the row sits under, so the filter and the page agree:
 * "AU-AA-608 Parkolás" is badged "Expense account" and shows up under
 * Expenses, not Operators — and typing "expense" narrows the menu to them.
 */
function counterpartyKindLabel(row) {
  if (row?.side !== 'operator') return 'Partner';
  return OPERATOR_TYPE_SHORT_LABELS[row?.operatorType] || OPERATOR_TYPE_SHORT_LABELS.operator;
}

/**
 * The operator side split into [suppliers, expense accounts].
 *
 * One pass, so a row lands in exactly one of the two and cannot land in
 * neither: two independent `.filter()` calls whose predicates drifted apart
 * would quietly drop a counterparty's money out of the report altogether,
 * which is the one thing this split must never do.
 *
 * A row with no `operatorType` is a supplier — every row was one before the
 * field existed, and an API response that predates it must render exactly as
 * it did.
 */
function partitionByOperatorType(rows) {
  const suppliers = [];
  const expenses = [];

  (rows || []).forEach((row) => {
    if (row?.operatorType === 'expense') expenses.push(row);
    else suppliers.push(row);
  });

  return [suppliers, expenses];
}

// Accumulate {currency → {gross, net, cash, paid, stillDue, bookings}}
// across a list of normalised rows: every section heading's totals, and the
// counterparty filter's selection totals. Always per currency — a bucket per
// currency, never one figure across them.
function rollupByCurrency(rows) {
  const map = new Map();
  rows.forEach((row) => {
    const cur = row.currency || '';
    let acc = map.get(cur);
    if (!acc) {
      acc = {
        gross: 0,
        net: 0,
        cash: 0,
        paid: 0,
        stillDue: 0,
        // Sum of per-row profit in this currency bucket. Rendered only
        // when the viewer holds CAN_SEE_GLOBAL_STATS — the rollup itself
        // is permission-agnostic; the UI consumer gates the cell.
        profit: 0,
        bookings: 0,
        // Set of source-line currencies that contributed across rows
        // in this currency bucket. `CurrencyStack` reads it to decide
        // whether to FX-annotate the bucket's value via `renderMoney`.
        sourceCurrencies: new Set(),
      };
      map.set(cur, acc);
    }
    acc.gross += row.gross;
    acc.net += row.net;
    acc.cash += row.cash;
    acc.paid += row.paid;
    acc.stillDue += row.stillDue;
    acc.profit += Number(row.profit) || 0;
    acc.bookings += row.amountOfBookings;
    (Array.isArray(row.sourceCurrencies) ? row.sourceCurrencies : []).forEach((c) => {
      if (c) acc.sourceCurrencies.add(c);
    });
  });
  return map;
}

// One vertical-stack of `<amount> <currency>` lines for a multi-currency
// rollup cell. Width-bounded so the section header stays tidy on a
// laptop screen.
function CurrencyStack({
  map, field, signClass,
}) {
  if (!map || map.size === 0) {
    return <span className="text-muted">—</span>;
  }
  return (
    <div className="font-monospace small text-end">
      {Array.from(map.entries()).map(([cur, agg]) => {
        const v = agg[field] ?? 0;
        const cls = signClass ? signClass(v) : '';
        // Section-header rollup: plain "amount currency" without FX
        // annotation. The rate is only surfaced in the per-booking
        // subtotal where it's most useful for audit.
        return (
          <div key={cur || 'unknown'} className={cls}>
            {`${thousandSeparator(v)} ${cur || ''}`.trim()}
          </div>
        );
      })}
    </div>
  );
}

CurrencyStack.defaultProps = {
  signClass: undefined,
};

/**
 * A set of rows' figures, one `CurrencyStack` per column: the strip on every
 * section heading AND in the selection totals. One component so the two can
 * never show different columns, or the same column formatted two ways.
 */
function RollupFigures({ rollup, canSeeProfit, className }) {
  return (
    <div className={`${className} gap-4 align-items-start`}>
      <div className="text-end">
        <div className="text-muted small text-uppercase">Gross</div>
        <CurrencyStack map={rollup} field="gross" />
      </div>
      <div className="text-end">
        <div className="text-muted small text-uppercase">Net</div>
        <CurrencyStack map={rollup} field="net" />
      </div>
      {canSeeProfit && (
        <div className="text-end">
          <div className="text-muted small text-uppercase">Profit</div>
          <CurrencyStack map={rollup} field="profit" signClass={profitColorClass} />
        </div>
      )}
      <div className="text-end">
        <div className="text-muted small text-uppercase">Cash Received</div>
        <CurrencyStack map={rollup} field="cash" />
      </div>
      <div className="text-end">
        <div className="text-muted small text-uppercase">Paid</div>
        <CurrencyStack map={rollup} field="paid" />
      </div>
      <div className="text-end">
        <div className="text-muted small text-uppercase">Still due</div>
        <CurrencyStack map={rollup} field="stillDue" signClass={stillColorClass} />
      </div>
    </div>
  );
}

RollupFigures.defaultProps = {
  className: 'd-flex',
};

// -----------------------------------------------------------------
// The counterparty filter
// -----------------------------------------------------------------
// WHY IT EXISTS (Mark, 2026-11-03: "it's very difficult to see all charges
// belonging to the operator. They have many buckets"). An own-fleet driver's
// money sits on several operator records, each its own row: the fee record
// ("AU-AA-670 - Tóth Imre"), the accounts he pays out of pocket ("Tóth Imre –
// Parkolás (EGYENI)", one per currency) and the company-paid ones (the Shell
// card, the rental: "… (PH)") — spread across Operators and Expenses. A
// third-party company can likewise have an EUR record and a `_HUF` one.
//
// NO GROUPING, deliberately ("No grouping for now"). The filter chooses WHICH
// rows show and nothing else: every row stays one record, with its own
// figures and its own Pay, CSV, PDF and Ledger, because each record is
// settled on its own — in its own settlement currency, and to its own payee
// (a "(PH)" account is paid to Shell or the rental firm, not to the driver).
// A merged "Tóth" row would add HUF to EUR and his money to Shell's. If
// grouping is ever wanted, it is a new decision, not an extension of this.
//
// It replaced the single-pick "Quick jump to a counterparty" box, which
// scrolled to one row and flashed it. One search box per screen: a selection
// of one IS a jump, and two boxes would be two answers to "find a row".

/**
 * Does a filter option answer what was typed?
 *
 * Every WORD must appear somewhere, in any order — "toth egyeni" finds
 * "Tóth Imre – Parkolás (EGYENI)", which one substring never could — and
 * each word is compared by the portal's `looseIncludes`, so case, accents and
 * punctuation are ignored: "toth" finds "Tóth", "egyeni" finds "(EGYENI)",
 * "au-aa 670" finds "AU-AA-670".
 *
 * It REPLACES the typeahead's own filter (a single accent-blind substring of
 * the label) rather than sitting beside it, because "Add all matching" has to
 * add exactly what the menu lists: with two matchers, one day they disagree.
 */
function matchesEveryWord(text, query) {
  return String(query || '').trim().split(/\s+/).every((word) => looseIncludes(text, word));
}

/**
 * One filter option per row of the period, carrying the row itself.
 *
 * `label` is what the filter searches: the name, the currency and the section
 * (`counterpartyKindLabel`), so "huf" or "expense" narrow the menu as well as
 * a name does. Sorted by absolute still-due, so the most unsettled records
 * surface first. That sort compares HUF numbers with EUR numbers — acceptable
 * for an ORDER, never for a total.
 */
function filterOptionsFor(rows) {
  return [...rows]
    .sort((a, b) => Math.abs(b.stillDue) - Math.abs(a.stillDue))
    .map((row) => ({
      ...row,
      label: [row.name, row.currency, counterpartyKindLabel(row)].filter(Boolean).join(' · '),
    }));
}

/** The rows a selection shows — every row when nothing is selected. */
function rowsInSelection(rows, selectedIds) {
  if (!selectedIds.length) return rows;
  const wanted = new Set(selectedIds);
  return rows.filter((row) => wanted.has(row.id));
}

/**
 * The name of a selected record the page has no row for, from the directory.
 *
 * The API leaves a record with nothing in the window out of the summary
 * altogether (its "empty-row prune"), so a selection opened from a reload or
 * a shared link can name a record this period has no row for, and so no
 * name. Its chip must not vanish or read as a bare id, so the name is looked
 * up in the operators / partners directory (`useCounterpartyNames`). Being a
 * component, it costs that request only when such a chip exists, and the hook
 * caches both lists app-wide. (A record picked earlier in this visit never
 * gets here: the page remembers the name it was picked by — `seenOptionsRef`.)
 *
 * An id that is no operator's is tried as a partner ROW id,
 * `${partner_id}-${currency}` (see `normalisePartnerRow`). One in neither
 * directory — a deactivated record — shows as its id rather than as nothing.
 */
function DirectoryName({ id }) {
  const { operatorsById, partnersById, loading } = useCounterpartyNames();
  const operatorName = counterpartyLabel(operatorsById.get(id));
  if (operatorName) return operatorName;
  const cut = id.lastIndexOf('-');
  const partnerName = cut > 0 ? counterpartyLabel(partnersById.get(id.slice(0, cut))) : '';
  // The currency always, here: which of a partner's rows this was is not
  // knowable without its row, and the id is all that is left of it.
  const currency = id.slice(cut + 1);
  if (partnerName) return currency ? `${partnerName} (${currency})` : partnerName;
  return loading ? '…' : id;
}

/**
 * The totals of the rows the filter shows: PER CURRENCY, and per SIDE.
 *
 * NEVER A GRAND TOTAL ACROSS CURRENCIES. Every row is already in its own
 * settlement currency, converted once by the API at the period-end anchor;
 * converting again here would put a second, disagreeing number on screen, and
 * adding without converting is nonsense. A selection of a HUF fee record and
 * an EUR expense account therefore shows two lines, one per currency.
 *
 * NEVER OPERATORS AND PARTNERS IN ONE FIGURE. An operator row is money we owe,
 * a partner row money owed to us; one sum would net a debt against a
 * receivable and show neither. Suppliers and expense accounts DO share a
 * figure: both are money we pay out, and seeing a driver's fee record beside
 * his expense accounts is what the filter is for.
 *
 * The sums are `rollupByCurrency` — the section headings' own — over the rows
 * on screen, so every total here is the sum of figures the reader can see.
 */
function SelectionTotals({ operatorSideRows, partnerRows, canSeeProfit }) {
  const sides = [
    {
      key: 'operator', title: 'Operators & expense accounts', hint: 'we pay', rows: operatorSideRows,
    },
    {
      key: 'partner', title: 'Partners', hint: 'they pay us', rows: partnerRows,
    },
  ].filter((side) => side.rows.length > 0);

  if (sides.length === 0) return null;

  return (
    <Card className="mb-3" data-testid="month-end-selection-totals">
      <Card.Body>
        <div className="fw-semibold mb-2">
          Selection totals
          <span className="ms-2 small text-muted fw-normal">
            per currency, never added across currencies
          </span>
        </div>
        {sides.map((side) => (
          <div
            key={side.key}
            className="d-flex flex-wrap justify-content-between align-items-start gap-3 py-2 border-top"
            data-testid={`month-end-selection-totals-${side.key}`}
          >
            <div>
              <div className="fw-semibold">{side.title}</div>
              <div className="small text-muted">
                {`${side.hint} · ${side.rows.length} ${side.rows.length === 1 ? 'row' : 'rows'}`}
              </div>
            </div>
            <RollupFigures
              rollup={rollupByCurrency(side.rows)}
              canSeeProfit={canSeeProfit}
              className="d-flex flex-wrap"
            />
          </div>
        ))}
      </Card.Body>
    </Card>
  );
}

// -----------------------------------------------------------------
// Click-to-sort header cell. Mirrors the `SortTh` pattern already in
// Pages/Stats/index.jsx — first click sorts DESC, subsequent clicks on
// the same column toggle ASC ↔ DESC. Inactive columns render a muted
// ↕ glyph so users can see the column is sortable; the active column
// renders fa-arrow-up / fa-arrow-down for the direction. The header
// cell still accepts arbitrary inline style + className so callers can
// keep the existing fixed-width / text-align styling on the outer
// table headers.
function SortTh({
  label,
  field,
  sort,
  onSort,
  className = '',
  style,
}) {
  const sortable = !!field;
  const active = sortable && sort.field === field;
  const handleClick = () => {
    if (!sortable) return;
    onSort({ field, dir: active && sort.dir === 'desc' ? 'asc' : 'desc' });
  };
  const cursorStyle = sortable
    ? { cursor: 'pointer', userSelect: 'none', ...(style || {}) }
    : (style || {});
  return (
    <th
      className={className}
      style={cursorStyle}
      onClick={sortable ? handleClick : undefined}
    >
      {label}
      {sortable && (
        <span className="ms-1">
          {active ? (
            <i className={`fa fa-arrow-${sort.dir === 'asc' ? 'up' : 'down'} fa-xs`} />
          ) : (
            <span className="text-muted" style={{ opacity: 0.5 }}>↕</span>
          )}
        </span>
      )}
    </th>
  );
}

SortTh.defaultProps = {
  className: '',
  style: undefined,
};

// Sort comparator for normalised counterparty rows. The `name` field is
// locale-aware alphabetical; everything else is numeric on the row's
// scalar. Multi-currency partner rows stay independent (we sort the
// per-currency rows by their own per-currency values; no cross-currency
// merge). `dir` is 'asc' | 'desc'.
function sortCounterpartyRows(rows, { field, dir }) {
  if (!field) return rows;
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (field === 'name') {
      const an = String(a.name || '');
      const bn = String(b.name || '');
      return factor * an.localeCompare(bn);
    }
    const av = Number(a[field]) || 0;
    const bv = Number(b[field]) || 0;
    if (av === bv) return 0;
    return factor * (av - bv);
  });
}

// -----------------------------------------------------------------
// Single counterparty row: collapsed shows totals; expanded drills
// into the per-booking ledger. The detail fetch is owned upstream
// (lazy + cached) — this component just dispatches expand/collapse
// and renders the result.
// -----------------------------------------------------------------
function CounterpartyRow({
  row,
  isExpanded,
  onToggle,
  detailState,
  setDrilldownTrip,
  onBulkPay,
  onDownloadCsv,
  onDownloadReport,
  onOpenLedger,
  fxRates,
  canSeeProfit,
}) {
  // The operator report is built on the server from Month End's own figures
  // and takes a few seconds (it recomputes the month), so the button shows it
  // is working rather than inviting a second click.
  const [reportBusy, setReportBusy] = useState(false);
  const handleReportClick = useCallback(async (e) => {
    e.stopPropagation();
    if (!onDownloadReport || reportBusy) return;
    setReportBusy(true);
    try {
      await onDownloadReport(row);
    } finally {
      setReportBusy(false);
    }
  }, [onDownloadReport, reportBusy, row]);
  // Local "CSV download in progress" flag so the button can spinner
  // when we have to lazy-fetch the detail before generating the file.
  const [csvBusy, setCsvBusy] = useState(false);
  const handleCsvClick = useCallback(async (e) => {
    e.stopPropagation();
    if (!onDownloadCsv || csvBusy) return;
    setCsvBusy(true);
    try {
      await onDownloadCsv(row);
    } finally {
      setCsvBusy(false);
    }
  }, [csvBusy, onDownloadCsv, row]);
  const stillCls = stillColorClass(row.stillDue);
  const isOperator = row.side === 'operator';

  // Local expanded-trip set so each counterparty's per-booking
  // expansion is independent of the others.
  const [expandedTrips, setExpandedTrips] = useState(() => new Set());
  const toggleTrip = useCallback((tripId) => {
    setExpandedTrips((prev) => {
      const next = new Set(prev);
      if (next.has(tripId)) next.delete(tripId);
      else next.add(tripId);
      return next;
    });
  }, []);

  const detail = detailState?.data;
  // For multi-currency partner rows (Fix 2) the same booking list is
  // shared across two rows; filter to just the trips whose currency
  // matches THIS row so the per-bucket detail reconciles with the
  // per-bucket totals on the row header. Operator side and
  // single-currency partner rows pass the full trips list through.
  const allTrips = Array.isArray(detail?.trips) ? detail.trips : [];
  const trips = (row.multiCurrency && !isOperator)
    ? allTrips.filter((t) => (t.currency || '') === row.currency)
    : allTrips;
  const payments = Array.isArray(detail?.payments) ? detail.payments : [];

  // Per-counterparty FX rates. Prefer the detail-response's `fx.rates`
  // (more focused / matches the trips we're rendering); fall back to
  // the summary-level rates passed in via prop so a not-yet-expanded
  // row's totals still annotate correctly. Empty object means
  // `renderMoney` will skip annotation, which is the right behaviour
  // for a single-currency operator with no source-currency mismatch.
  const detailFxRates = detail?.fx?.rates;
  const effectiveFxRates = detailFxRates || fxRates || {};

  // Union of source currencies across this row's trips. Used as the
  // hint for the row's aggregated totals so a HUF-settling operator
  // with both EUR + HUF bookings shows "(@ EUR→HUF <rate>)" only when
  // at least one trip crossed currencies. Prefer the summary-level
  // hint (`row.sourceCurrencies`, populated by the backend rollup) so
  // we can annotate collapsed rows BEFORE the per-counterparty detail
  // has been lazy-loaded; once it's loaded we recompute from the trip
  // (aggregate source currencies are no longer used at the row level
  // — the main counterparty row deliberately renders plain
  // amount+currency without FX annotation; the rate is surfaced
  // inside the expanded per-booking ledger subtotal instead.)

  return (
    <>
      <tr
        className={[
          'month-end-counterparty-row',
          isExpanded ? 'month-end-counterparty-row-expanded' : '',
        ].filter(Boolean).join(' ')}
        onClick={onToggle}
      >
        <td style={{ width: 28 }} className="text-center text-muted">
          <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
        </td>
        <td className="fw-semibold">
          {row.name}
          {row.multiCurrency && row.currency && (
            <span className="ms-2 small text-muted">{`(${row.currency})`}</span>
          )}
        </td>
        {/* Main counterparty columns: plain "amount currency" without
            FX annotations. The FX rate is shown only inside the
            expanded per-booking ledger (subtotal row), where it's
            most useful for line-level audit. */}
        <td className="text-end font-monospace">
          {`${thousandSeparator(row.gross)} ${row.currency || ''}`.trim()}
        </td>
        <td className="text-end font-monospace">
          {`${thousandSeparator(row.net)} ${row.currency || ''}`.trim()}
        </td>
        {canSeeProfit && (() => {
          const marginPct = profitMarginPct(row);
          const tone = profitToneClass(marginPct);
          const marginSuffix = marginPct == null ? '' : ` · ${Math.round(marginPct)}%`;
          return (
            <td className={`text-end font-monospace ${tone}`}>
              {`${thousandSeparator(row.profit)} ${row.currency || ''}`.trim()}
              {marginSuffix && (
                <span className="ms-1 small">{marginSuffix}</span>
              )}
            </td>
          );
        })()}
        <td className="text-end font-monospace text-muted">
          {Math.abs(row.cash) > ZERO_EPSILON
            ? `−${thousandSeparator(row.cash)} ${row.currency || ''}`.trim()
            : '—'}
        </td>
        <td className="text-end font-monospace">
          {`${thousandSeparator(row.paid)} ${row.currency || ''}`.trim()}
        </td>
        <td className={`text-end fw-bold font-monospace ${stillCls}`}>
          {`${thousandSeparator(row.stillDue)} ${row.currency || ''}`.trim()}
        </td>
        <td className="text-end">{row.amountOfBookings}</td>
        <td className="text-end" onClick={(e) => e.stopPropagation()}>
          <div className="d-flex gap-1 justify-content-end">
            {onBulkPay && (
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => onBulkPay(row)}
                title={`Record a bulk payment to ${row.name} for the selected period`}
              >
                <i className="fa fa-money-bill-wave me-1" />
                Pay
              </Button>
            )}
            {onOpenLedger && (
              <OverlayTrigger
                placement="top"
                overlay={(
                  <Tooltip>
                    Every money movement with this counterparty, all time,
                    showing how each payment was allocated
                  </Tooltip>
                )}
              >
                <Button
                  size="sm"
                  variant="outline-dark"
                  onClick={() => onOpenLedger(row)}
                >
                  <i className="fa fa-book me-1" />
                  Ledger
                </Button>
              </OverlayTrigger>
            )}
            {onDownloadCsv && (
              <OverlayTrigger
                placement="top"
                overlay={(
                  <Tooltip>
                    Download a CSV of this counterparty&apos;s
                    outstanding amounts for the selected period
                  </Tooltip>
                )}
              >
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleCsvClick}
                  disabled={csvBusy}
                >
                  {csvBusy ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <i className="fa fa-file-csv me-1" />
                  )}
                  CSV
                </Button>
              </OverlayTrigger>
            )}
            {/* Operator rows only — suppliers AND expense accounts: the
                report is one operator record's settlement statement. A
                partner row has no such statement. */}
            {onDownloadReport && isOperator && (
              <OverlayTrigger
                placement="top"
                overlay={(
                  <Tooltip>
                    Settlement statement (PDF) for exactly this row and the
                    selected period: trips, costs, cash and payments, month
                    by month
                  </Tooltip>
                )}
              >
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={handleReportClick}
                  disabled={reportBusy}
                  aria-label={`Download the Month End report of ${row.name} as PDF`}
                >
                  {reportBusy ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <i className="fa fa-file-pdf me-1" />
                  )}
                  PDF
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="month-end-expansion-row">
          {/* Expansion cell spans every visible column — Profit adds one
              extra slot when CAN_SEE_GLOBAL_STATS is granted. */}
          <td colSpan={canSeeProfit ? 10 : 9} className="month-end-expansion-cell">
            {detailState?.loading && (
              <div className="d-flex justify-content-center align-items-center py-3">
                <Spinner animation="border" size="sm" />
                <span className="ms-2 text-muted">Loading bookings…</span>
              </div>
            )}
            {detailState?.error && (
              <Error error={detailState.error} />
            )}
            {!detailState?.loading && !detailState?.error && detail && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="text-muted small">
                    {`${trips.length} bookings`}
                  </div>
                  {trips.length > 0 && (
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setExpandedTrips(new Set(
                          trips.map((t) => t._id).filter(Boolean),
                        ))}
                        disabled={expandedTrips.size === trips.length}
                      >
                        Expand all
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setExpandedTrips(new Set())}
                        disabled={expandedTrips.size === 0}
                      >
                        Collapse all
                      </Button>
                    </div>
                  )}
                </div>
                <Table
                  responsive
                  size="sm"
                  hover
                  className="month-end-inner-table align-middle small mb-0"
                >
                  <thead>
                    <tr>
                      <th style={{ width: 28 }} aria-label="Expand toggle" />
                      <th>Booking</th>
                      <th style={{ width: 110 }}>Status</th>
                      <th style={{ width: 100 }}>Date</th>
                      <th style={{ width: 100 }}>Type</th>
                      <th style={{ width: 110 }}>Class</th>
                      <th className="text-end" style={{ width: 130 }}>Cash</th>
                      <th className="text-end" style={{ width: 110 }}>Gross</th>
                      <th className="text-end" style={{ width: 110 }}>Paid</th>
                      <th className="text-end" style={{ width: 130 }}>Still due</th>
                      <th className="text-end" style={{ width: 90 }} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {trips.flatMap((trip) => {
                      const isTripExpanded = expandedTrips.has(trip._id);
                      const { summary, details } = isOperator
                        ? operatorSideRowsForTrip(
                          trip,
                          payments,
                          isTripExpanded,
                          () => toggleTrip(trip._id),
                          setDrilldownTrip,
                          effectiveFxRates,
                          row.id,
                        )
                        : partnerSideRowsForTrip(
                          trip,
                          payments,
                          isTripExpanded,
                          () => toggleTrip(trip._id),
                          setDrilldownTrip,
                          effectiveFxRates,
                          row.id,
                        );
                      return isTripExpanded ? [summary, ...details] : [summary];
                    })}
                  </tbody>
                </Table>
                {trips.length === 0 && (
                  <div className="text-muted text-center py-3">
                    No bookings for this counterparty in the selected period.
                  </div>
                )}
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// -----------------------------------------------------------------
// Section accordion: header (with rollup totals) + body (rows). We
// fold the table itself into the section body so the section header
// can carry its own per-currency totals, and the body's column widths
// align with the counterparty rows below.
//
// `rows` are the rows ON SCREEN — with the counterparty filter on, only the
// selected ones — and the heading's totals are theirs, so a heading always
// sums what sits under it. `totalCount` is how many the section has before
// the filter, or null with no filter on; it is what turns the count badge
// into "2 of 41 shown".
// -----------------------------------------------------------------
function SectionAccordion({
  title,
  icon,
  rows,
  totalCount,
  isOpen,
  onToggle,
  expandedRowId,
  toggleRow,
  detailStateById,
  setDrilldownTrip,
  onBulkPay,
  onDownloadCsv,
  onDownloadReport,
  onOpenLedger,
  fxRates,
  canSeeProfit,
}) {
  // Per-section sort state. Each SectionAccordion (operator + partner)
  // owns its own so sorting the operator table doesn't affect the
  // partner table. `field` null means "leave row order alone" — falls
  // through to the backend's pre-sorted order. First click on any
  // header lands on DESC; subsequent clicks toggle ASC ↔ DESC (same
  // convention as Pages/Stats/index.jsx).
  const [sort, setSort] = useState({ field: null, dir: 'desc' });
  const sortedRows = useMemo(
    () => sortCounterpartyRows(rows, sort),
    [rows, sort],
  );
  const rollup = useMemo(() => rollupByCurrency(rows), [rows]);
  const filtered = totalCount !== null;
  return (
    <Card className="mb-3">
      <Card.Body className="p-0">
        <div
          className="d-flex justify-content-between align-items-center p-3"
          style={{ cursor: 'pointer', background: isOpen ? '#f4f7fb' : 'white' }}
          onClick={onToggle}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
        >
          <div className="d-flex align-items-center">
            <i className={`fa ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} />
            {icon && <i className={`${icon} me-2`} />}
            <h4 className="mb-0">{title}</h4>
            <Badge bg={filtered ? 'warning' : 'light'} text="dark" className="ms-2">
              {filtered
                ? `${rows.length} of ${totalCount} shown`
                : `${rows.length} ${rows.length === 1 ? 'counterparty' : 'counterparties'}`}
            </Badge>
          </div>
          <RollupFigures rollup={rollup} canSeeProfit={canSeeProfit} className="d-none d-md-flex" />
        </div>
        {isOpen && (
          <div className="border-top">
            {rows.length === 0 ? (
              <div className="text-muted text-center py-4">
                {filtered
                  ? 'None of the selected counterparties are in this section.'
                  : 'No counterparties in this section for the selected period.'}
              </div>
            ) : (
              <Table responsive hover className="month-end-outer-table mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 28 }} aria-label="Expand toggle" />
                    <SortTh label="Counterparty" field="name" sort={sort} onSort={setSort} />
                    <SortTh label="Gross" field="gross" sort={sort} onSort={setSort} className="text-end" style={{ width: 130 }} />
                    <SortTh label="Net" field="net" sort={sort} onSort={setSort} className="text-end" style={{ width: 130 }} />
                    {canSeeProfit && (
                      <SortTh label="Profit" field="profit" sort={sort} onSort={setSort} className="text-end" style={{ width: 130 }} />
                    )}
                    <SortTh label="Cash Received" field="cash" sort={sort} onSort={setSort} className="text-end" style={{ width: 130 }} />
                    <SortTh label="Paid so far" field="paid" sort={sort} onSort={setSort} className="text-end" style={{ width: 130 }} />
                    <SortTh label="Still due" field="stillDue" sort={sort} onSort={setSort} className="text-end" style={{ width: 150 }} />
                    <SortTh label="# bookings" field="amountOfBookings" sort={sort} onSort={setSort} className="text-end" style={{ width: 90 }} />
                    <th className="text-end" style={{ width: 110 }} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => (
                    <CounterpartyRow
                      key={`${row.side}-${row.id}`}
                      row={row}
                      isExpanded={expandedRowId === row.id}
                      onToggle={() => toggleRow(row.id)}
                      // Detail is keyed by `fetchId` (the bare
                      // partner_id / operator_id) so multi-currency
                      // partner rows share one fetched booking list.
                      detailState={detailStateById.get(row.fetchId)}
                      setDrilldownTrip={setDrilldownTrip}
                      onBulkPay={onBulkPay}
                      onDownloadCsv={onDownloadCsv}
                      onDownloadReport={onDownloadReport}
                      onOpenLedger={onOpenLedger}
                      fxRates={fxRates}
                      canSeeProfit={canSeeProfit}
                    />
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function MonthEnd() {
  // -----------------------------------------------------------------
  // Permissions
  // -----------------------------------------------------------------
  // CAN_SEE_GLOBAL_STATS gates revenue/profit visibility everywhere in
  // the dashboard. The Stats page checks the same flag (see
  // src/Pages/Stats/index.jsx) — we mirror the pattern here so the
  // Month End Profit column hides cleanly for AgentHead / Agent users.
  const canSeeProfit = UserService.getInstance().canAccessModule(
    Permissions.CAN_SEE_GLOBAL_STATS,
  );

  // -----------------------------------------------------------------
  // Period state
  // -----------------------------------------------------------------
  const [month, setMonth] = useState(defaultMonthString());
  const [showRangeRefiner, setShowRangeRefiner] = useState(false);
  const [fromOverride, setFromOverride] = useState('');
  const [toOverride, setToOverride] = useState('');

  const { from, to } = useMemo(() => {
    const derived = deriveRangeFromMonth(month);
    return {
      from: fromOverride || derived.from,
      to: toOverride || derived.to,
    };
  }, [month, fromOverride, toOverride]);

  // -----------------------------------------------------------------
  // Summary state (drives the sections + the counterparty filter)
  // -----------------------------------------------------------------
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  // Each section can be expanded / collapsed independently. Default
  // open so the operator sees everything at first paint (matches the
  // balance-sheet brief).
  //
  // Expenses defaults CLOSED, and it is the only one that does. It is a
  // short list of internal cost buckets that reconcile against nothing and
  // nobody chases; opening the page on it would push the suppliers — the
  // reason anyone is here — below the fold. One click still opens it.
  const [operatorsOpen, setOperatorsOpen] = useState(true);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [partnersOpen, setPartnersOpen] = useState(true);

  // Per-section "which counterparty row is currently expanded" — only
  // one at a time to keep the page short. (Tradeoff vs. multi-expand:
  // multi-expand would let you eyeball two counterparties side-by-side
  // but pile a lot of network traffic + DOM weight on the page. The
  // brief asks for a balance-sheet feel; one open row keeps the focus
  // on the row you just clicked.)
  const [expandedOperatorId, setExpandedOperatorId] = useState(null);
  const [expandedPartnerId, setExpandedPartnerId] = useState(null);

  // Per-counterparty detail cache: id → { loading, error, data }.
  // Keyed on counterparty id only; the entire cache is busted whenever
  // the active period changes (see useEffect below). Operators and
  // partners share the cache because their ids are disjoint UUIDs.
  const [detailById, setDetailById] = useState(() => new Map());

  // In-flight detail-fetch promises keyed by counterparty fetchId. Lets
  // the CSV-download handler share a single network request with the
  // row-expand handler when the user clicks both nearly at once. Cleared
  // alongside `detailById` on period change.
  const pendingDetailRef = useRef(new Map());

  // Drill-down trip: Order Details on its Order Ledger tab, read-only.
  const [drilldownTrip, setDrilldownTrip] = useState(null);

  // Bulk Payment modal target. Holds the {kind, id, currency, name} of
  // the row that opened it so the modal is prefilled with the right
  // counterparty + currency. Direction defaults to 'out' (paying
  // operator out / refunding partner — operator settlement is the
  // dominant use case from this page).
  const [bulkPaymentTarget, setBulkPaymentTarget] = useState(null);

  // Counterparty whose full money-movement ledger is open.
  const [ledgerTarget, setLedgerTarget] = useState(null);

  // The counterparty filter's selection: ROW ids (`row.id`), in the URL as
  // `?counterparties=[…]` through the same `useUrlViewState` the list
  // screens use, so it survives a reload and a link to it can be sent.
  //
  // It is deliberately NOT cleared when the period changes — stepping
  // through the months looking at the same records is the point — so an id
  // can name a record with no row in the new period (see `renderFilterChip`).
  // The PERIOD is not in the URL: Month End never kept it there, so a shared
  // link opens on the current month with the sender's selection.
  //
  // Anything in the param that is not a list of non-empty strings (a
  // hand-edited link) reads as no selection rather than breaking the page.
  const { view: filterView, setView: setFilterView } = useUrlViewState(FILTER_URL_DEFAULTS);
  const selectedIds = useMemo(() => (
    Array.isArray(filterView.counterparties)
      ? filterView.counterparties.filter((id) => typeof id === 'string' && id)
      : []
  ), [filterView.counterparties]);
  const filterActive = selectedIds.length > 0;
  // What is typed in the filter box, mirrored here because "Add all
  // matching" has to know what it would add. The typeahead empties its own
  // text on every pick without telling us, so every pick empties this too.
  const [filterText, setFilterText] = useState('');
  const filterRef = useRef(null);

  // -----------------------------------------------------------------
  // Fetchers
  // -----------------------------------------------------------------
  const fetchSummary = async ({ refresh = false } = {}) => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const response = await get('/stats', {
        type: 'month_end_summary',
        from,
        to,
        // refresh=1 tells the backend to bypass the 5-min summary cache.
        // Used right after recording a payment so the row's Paid /
        // Still due reflect the new state without a 5-min stale window.
        ...(refresh ? { refresh: '1' } : {}),
      });
      setSummary(response.data?.data || null);
    } catch (err) {
      setSummary(null);
      setSummaryError(
        err.response?.data?.message
          || 'Failed to load month-end summary (endpoint may not be deployed yet).',
      );
    } finally {
      setSummaryLoading(false);
    }
  };

  // Fetch a single counterparty's per-booking detail. Resolves the
  // matching operator_payment_stats or partner_trips_stats endpoint.
  // Memoised in the cache by `counterparty.id` — the cache is cleared
  // on period change so we never serve stale data across months.
  //
  // Returns the loaded `data` object on success (null on error) so
  // callers that need to use the data inline (e.g. the CSV download
  // handler) can `await` the fetch without a separate effect.
  const fetchDetailFor = useCallback((cp) => {
    if (!cp) return Promise.resolve(null);
    // Dedupe in-flight fetches: if a request for the same id is already
    // running, hand back its promise so the CSV button can await the
    // same response that the row-expand kicked off.
    const inflight = pendingDetailRef.current.get(cp.id);
    if (inflight) return inflight;

    setDetailById((prev) => {
      const next = new Map(prev);
      next.set(cp.id, { loading: true, error: '', data: prev.get(cp.id)?.data || null });
      return next;
    });

    const promise = (async () => {
      try {
        const startDate = dayjs.tz(from, DEFAULT_TIMEZONE).startOf('day');
        const endDate = dayjs.tz(to, DEFAULT_TIMEZONE).endOf('day');
        const isOperator = cp.side === 'operator';
        const response = await get('/stats', {
          type: isOperator ? 'operator_payment_stats' : 'partner_trips_stats',
          filters: {
            date: [toUtcIsoString(startDate), toUtcIsoString(endDate)],
            pageSize: 10000,
            status: [
              'Confirmed', 'Completed', 'Cancelled', 'Passenger No-Show', 'Driver No-Show',
            ],
            sort: { date_datetime: 'desc' },
            ...(isOperator
              ? { operators: [cp.id], show_profit: false }
              : { partners: [cp.id] }),
          },
        });
        const data = response.data?.data || null;
        setDetailById((prev) => {
          const next = new Map(prev);
          next.set(cp.id, { loading: false, error: '', data });
          return next;
        });
        return data;
      } catch (err) {
        setDetailById((prev) => {
          const next = new Map(prev);
          next.set(cp.id, {
            loading: false,
            error: err.response?.data?.message || 'Failed to load counterparty drill-down.',
            data: null,
          });
          return next;
        });
        return null;
      } finally {
        pendingDetailRef.current.delete(cp.id);
      }
    })();
    pendingDetailRef.current.set(cp.id, promise);
    return promise;
  }, [from, to]);

  // Toggle a row open/closed. Opening triggers lazy-fetch (skipped if
  // we already have data for that fetchId in the cache).
  //
  // After Fix 2, partner row `id` is `${partner_id}-${currency}` while
  // the backend fetch keys off the bare partner_id. Multi-currency
  // rows for the same partner share the detail cache (they all fetch
  // by partner_id). For operator rows the two ids coincide.
  const toggleRowFactory = (side) => (id) => {
    const setter = side === 'operator' ? setExpandedOperatorId : setExpandedPartnerId;
    const current = side === 'operator' ? expandedOperatorId : expandedPartnerId;
    if (current === id) {
      setter(null);
      return;
    }
    setter(id);
    // Recover the backend fetch id from the row id. For operators the
    // row id IS the operator_id; for partners we strip the trailing
    // -CURRENCY suffix by looking up the source summary row.
    let fetchId;
    let name;
    let currency;
    if (side === 'operator') {
      const cp = (summary?.operators || []).find((o) => o.operator_id === id);
      if (!cp) return;
      fetchId = cp.operator_id;
      name = cp.name;
      currency = cp.settlement_currency;
    } else {
      const cp = (summary?.partners || []).find(
        (p) => `${p.partner_id}-${p.currency || ''}` === id,
      );
      if (!cp) return;
      fetchId = cp.partner_id;
      name = cp.name;
      currency = cp.currency;
    }
    // Lazy-load: only fetch if we don't already have detail cached
    // for this counterparty (keyed by fetchId so multi-currency rows
    // share the same fetched booking list).
    const cached = detailById.get(fetchId);
    if (!cached || (!cached.data && !cached.loading)) {
      fetchDetailFor({
        side, id: fetchId, name, currency,
      });
    }
  };

  // Download a per-counterparty CSV for the active period. Lazily
  // fetches the booking detail if it isn't already loaded (sharing the
  // in-flight promise registry so we never double-fetch), then builds
  // the CSV client-side from `trip.order_lines` + the already-loaded
  // `payments` array. No new backend endpoint.
  const downloadCsv = useCallback(async (row) => {
    if (!row) return;
    let data = detailById.get(row.fetchId)?.data;
    if (!data) {
      data = await fetchDetailFor({
        side: row.side, id: row.fetchId, name: row.name, currency: row.currency,
      });
    }
    if (!data) return;
    const allTrips = Array.isArray(data.trips) ? data.trips : [];
    const payments = Array.isArray(data.payments) ? data.payments : [];
    // Multi-currency partner rows render only the subset of trips that
    // match this row's currency bucket — mirror the same filter the
    // in-row expansion uses so the CSV reconciles with the on-screen
    // row totals.
    const trips = (row.multiCurrency && row.side !== 'operator')
      ? allTrips.filter((t) => (t.currency || '') === row.currency)
      : allTrips;
    const csvText = buildCounterpartyCsv({
      row,
      trips,
      payments,
      fromStr: from,
      toStr: to,
      // Same anchor rates the on-screen ledger uses, so a payment made
      // in a currency other than the row's settlement currency is
      // converted rather than dropped from Paid.
      fxRates: data.fx?.rates || {},
    });
    const filename = `month-end-${slugifyName(row.name)}-${from}-${to}.csv`;
    triggerCsvDownload(filename, csvText);
  }, [detailById, fetchDetailFor, from, to]);

  // The Month End operator report (PDF) of one operator row, for exactly the
  // period on screen. Built by the API from the same functions as the row
  // (`type=month_end_operator_report`), month by month, so each month of it
  // is this screen's row for that month — never computed here. The row id IS
  // the operator record: Month End lists each record of a linked pair as its
  // own row, and each gets its own report.
  const [reportError, setReportError] = useState('');
  const downloadReport = useCallback(async (row) => {
    setReportError('');
    try {
      const response = await get('/stats', {
        type: 'month_end_operator_report',
        operator_id: row.id,
        from,
        to,
        format: 'pdf',
      });
      downloadPdf(response.data?.data, `month-end-report-${slugifyName(row.name)}-${from}-${to}.pdf`);
    } catch (err) {
      setReportError(
        `Couldn't build the PDF report of ${row.name}: ${err.response?.data?.message || err.message}`,
      );
    }
  }, [from, to]);

  // Refetch the month-end summary whenever the effective period
  // changes. Drill-down cache is busted in the same effect so we
  // never serve stale ledger rows across months.
  useEffect(() => {
    // Drop the previous period's rollup BEFORE refetching. The detail
    // cache is cleared synchronously here while `fetchSummary` only
    // replaces `summary` once its request resolves, so leaving the old
    // object in place rendered the previous period's aggregate above a
    // freshly-fetched drill-down: Hungária Group showed a 13-booking /
    // 372,450 HUF header over an 11-booking / 313,350 HUF list, and
    // finance spent an afternoon reconciling the 59,100 difference.
    // Clearing it here means the section shows its loading state
    // instead of numbers for a window the user is no longer viewing.
    setSummary(null);
    fetchSummary();
    setDetailById(new Map());
    pendingDetailRef.current = new Map();
    setExpandedOperatorId(null);
    setExpandedPartnerId(null);
  }, [from, to]);

  // -----------------------------------------------------------------
  // Derived rows + rollups
  // -----------------------------------------------------------------
  const operatorRows = useMemo(() => {
    if (!summary) return [];
    return (summary.operators || []).map(normaliseOperatorRow);
  }, [summary]);

  // The operator side, filed under two headings instead of one.
  //
  // PRESENTATION ONLY. `operatorRows` above is untouched and is still the
  // whole operator side; these two are a partition of it, so every figure in
  // them is the same number it was, and the two rollups still add up to what
  // the single Operators rollup showed. Nothing is dropped: `partition`
  // cannot lose a row, which is why it is written as one pass rather than two
  // filters that could disagree.
  const [supplierRows, expenseRows] = useMemo(
    () => partitionByOperatorType(operatorRows),
    [operatorRows],
  );

  const partnerRows = useMemo(() => {
    if (!summary) return [];
    // Backend (Fix 2) emits one row per (partner_id, currency). Count
    // partners with >1 row so the label can be parenthesised by
    // currency only on multi-currency partners. Single-currency
    // partners stay labelled by name only.
    const partnerCount = new Map();
    (summary.partners || []).forEach((p) => {
      partnerCount.set(p.partner_id, (partnerCount.get(p.partner_id) ?? 0) + 1);
    });
    return (summary.partners || []).map(
      (p) => normalisePartnerRow(p, (partnerCount.get(p.partner_id) ?? 1) > 1),
    );
  }, [summary]);

  // Response-level FX rates (used by `renderMoney` to append the
  // `(@ SRC→DST <rate>)` annotation). Empty object means no FX was
  // applied or the API hasn't shipped the new field yet — `renderMoney`
  // degrades gracefully to the raw amount.
  const summaryFxRates = useMemo(
    () => summary?.fx?.rates || {},
    [summary],
  );

  // -----------------------------------------------------------------
  // The counterparty filter (see its block above `matchesEveryWord` for
  // why it filters and never groups)
  // -----------------------------------------------------------------
  const filterOptions = useMemo(
    () => filterOptionsFor([...operatorRows, ...partnerRows]),
    [operatorRows, partnerRows],
  );
  const filterOptionsById = useMemo(
    () => new Map(filterOptions.map((option) => [option.id, option])),
    [filterOptions],
  );

  // Every option seen this visit, by id. A selected record then keeps the
  // name it was picked by in a period where it has no row — and while a new
  // period loads, since the summary is dropped before every refetch (the
  // Hungária note in the period effect) — with no directory lookup and no
  // chip flickering to "…" and back on every month change.
  const seenOptionsRef = useRef(new Map());
  useEffect(() => {
    filterOptions.forEach((option) => seenOptionsRef.current.set(option.id, option));
  }, [filterOptions]);

  // The chips, in the order picked. A selected id with no row this period is
  // an `inactive` chip: still there and removable, never silently gone.
  const selectedOptions = useMemo(() => selectedIds.map((id) => (
    filterOptionsById.get(id) || {
      ...seenOptionsRef.current.get(id), id, label: id, inactive: true,
    }
  )), [selectedIds, filterOptionsById]);
  const selectedOptionsById = useMemo(
    () => new Map(selectedOptions.map((option) => [option.id, option])),
    [selectedOptions],
  );

  // ONE predicate for "the menu offers this row" — the typeahead's
  // `filterBy` and "Add all matching" both call it, so the entry can never
  // add a row the menu did not list, or miss one it did.
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const isOffered = useCallback(
    (option, text) => !selectedIdSet.has(option.id) && matchesEveryWord(option.label, text),
    [selectedIdSet],
  );
  const matchingIds = useMemo(() => (
    filterText.trim()
      ? filterOptions.filter((option) => isOffered(option, filterText)).map((option) => option.id)
      : []
  ), [filterOptions, filterText, isOffered]);

  // "Add all N matching" heads the menu once the text matches more than one
  // row: type "Tóth", take every record of his in one go. A way of PICKING,
  // not a group — it adds the matching rows' ids, and each is its own chip
  // and its own row from then on. Its `label` is the whole sentence because
  // the typeahead makes every entry's label its aria-label.
  const menuOptions = useMemo(() => (
    matchingIds.length > 1
      ? [{ addAll: true, label: `Add all ${matchingIds.length} matching “${filterText.trim()}”` }, ...filterOptions]
      : filterOptions
  ), [filterOptions, filterText, matchingIds]);

  const onFilterChange = (picked) => {
    const ids = picked.flatMap((option) => (option.addAll ? matchingIds : [option.id]));
    setFilterView({ counterparties: [...new Set(ids)] });
    setFilterText('');
  };

  const clearFilter = () => {
    filterRef.current?.clear();
    setFilterView({ counterparties: [] });
    setFilterText('');
  };

  const visibleSupplierRows = useMemo(
    () => rowsInSelection(supplierRows, selectedIds),
    [supplierRows, selectedIds],
  );
  const visibleExpenseRows = useMemo(
    () => rowsInSelection(expenseRows, selectedIds),
    [expenseRows, selectedIds],
  );
  const visiblePartnerRows = useMemo(
    () => rowsInSelection(partnerRows, selectedIds),
    [partnerRows, selectedIds],
  );
  const visibleOperatorSideRows = useMemo(
    () => [...visibleSupplierRows, ...visibleExpenseRows],
    [visibleSupplierRows, visibleExpenseRows],
  );
  const visibleCount = visibleOperatorSideRows.length + visiblePartnerRows.length;
  // Only once the period has loaded: before that, nobody knows yet.
  const inactiveCount = summary ? selectedOptions.filter((option) => option.inactive).length : 0;

  // A section with selected rows in it opens. Expenses starts CLOSED (see
  // its state above), and a filter that picked an expense account must not
  // leave it behind a closed heading. Keyed on WHICH rows are selected in
  // the section, so it fires on a pick, when a reload or a shared link's
  // summary lands, and on a period change — and does not re-open a section
  // the reader closed while the selection in it stayed the same.
  const selectionKey = (rows) => (filterActive ? rows.map((row) => row.id).sort().join(',') : '');
  const selectedSupplierKey = selectionKey(visibleSupplierRows);
  const selectedExpenseKey = selectionKey(visibleExpenseRows);
  const selectedPartnerKey = selectionKey(visiblePartnerRows);
  useEffect(() => { if (selectedSupplierKey) setOperatorsOpen(true); }, [selectedSupplierKey]);
  useEffect(() => { if (selectedExpenseKey) setExpensesOpen(true); }, [selectedExpenseKey]);
  useEffect(() => { if (selectedPartnerKey) setPartnersOpen(true); }, [selectedPartnerKey]);

  // -----------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------
  // A menu entry: name, the section it sits under (the badge), currency,
  // and the figures to tell two similar records apart.
  const renderFilterOption = (option) => {
    if (option.addAll) {
      return (
        <span className="fw-semibold text-primary">
          <i className="fa fa-plus-circle me-2" />
          {option.label}
        </span>
      );
    }
    const kindVariant = option.side === 'partner'
      ? 'primary'
      : option.operatorType === 'expense' ? 'secondary' : 'info';
    const stillCls = stillColorClass(option.stillDue);
    const stillDueSentence = option.side === 'operator'
      ? `we owe them ${thousandSeparator(option.stillDue)} ${option.currency || ''}`
      : `${thousandSeparator(option.stillDue)} ${option.currency || ''} still due`;
    return (
      <div className="d-flex justify-content-between align-items-center w-100">
        <div className="me-3">
          <div className="fw-semibold">
            {option.name}
            <Badge bg={kindVariant} className="ms-2">{counterpartyKindLabel(option)}</Badge>
            {option.currency && (
              <Badge bg="light" text="dark" className="ms-1">{option.currency}</Badge>
            )}
          </div>
          <div className="small text-muted">{stillDueSentence}</div>
        </div>
        <div className="text-end small font-monospace" style={{ minWidth: 240 }}>
          <div>
            <span className="text-muted">Owed </span>
            {`${thousandSeparator(option.gross)} ${option.currency || ''}`}
          </div>
          <div>
            <span className="text-muted">Paid </span>
            {`${thousandSeparator(option.paid)} ${option.currency || ''}`}
          </div>
          <div className={stillCls}>
            <span className="text-muted">Still due </span>
            {`${thousandSeparator(option.stillDue)} ${option.currency || ''}`}
          </div>
        </div>
      </div>
    );
  };

  // A chip reads exactly as its row's name does: the name as stored, plus
  // "(EUR)" only on a partner with rows in two currencies. Operator names
  // already carry the office's own decorations ("Diós Imre - EUR",
  // "…_HUF"), so a currency on every chip would say it twice.
  //
  // An `inactive` chip (no row this period) is muted and says why it shows
  // nothing — once the period has loaded. Its name comes from this visit's
  // memory, else from the directory (`DirectoryName`), which is asked only
  // once the summary has confirmed the row really is absent.
  //
  // The chip is drawn from the CURRENT selection, looked up by id, never from
  // the object the typeahead hands over. The typeahead renders chips from its
  // own copy of the selection, which catches up with `selected` one render
  // late: for that frame it passed the "Add all" entry just clicked (no id —
  // it crashed the directory lookup) and, on a reload, the placeholders from
  // before the summary landed (which fetched the whole directory for records
  // that turned out to have rows). An id not in the selection is not drawn.
  const renderFilterChip = (token, props) => {
    const option = selectedOptionsById.get(token.id);
    if (!option) return null;
    const inactiveHere = option.inactive && !!summary;
    let name = '…';
    if (option.name) name = option.multiCurrency ? `${option.name} (${option.currency})` : option.name;
    else if (summary) name = <DirectoryName id={option.id} />;
    return (
      <Token
        key={option.id}
        // The typeahead's own object: removal finds the chip in its copy of
        // the selection by deep equality.
        option={token}
        onRemove={props.onRemove}
        tabIndex={props.tabIndex}
        disabled={props.disabled}
        className={inactiveHere ? 'month-end-filter-chip-inactive' : undefined}
      >
        {name}
        {inactiveHere && <span className="ms-1 fst-italic">· no activity in this period</span>}
      </Token>
    );
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  return (
    <>
      {/* MonthEnd-scoped table palette + the filter's inactive chip.
          Kept inline so the rules live next to the class names they
          pair with. The .month-end-outer-table / .month-end-inner-table
          overrides are intentionally aggressive — they need to defeat
          the global "dark gradient thead" rule in App.scss without
          touching that stylesheet (other pages still rely on it). */}
      <style>
        {`
          /* ---------- Outer counterparty table (per section) ---------- */
          .month-end-outer-table {
            background: white;
          }
          .month-end-outer-table thead th {
            background: #f7f8fa !important;
            color: #495057 !important;
            font-weight: 600 !important;
            font-size: 0.75rem !important;
            text-transform: uppercase !important;
            letter-spacing: 0.3px !important;
            border: none !important;
            border-bottom: 1px solid #e6e9ee !important;
            padding: 0.65rem 0.75rem !important;
          }
          .month-end-outer-table tbody tr.month-end-counterparty-row {
            cursor: pointer;
          }
          .month-end-outer-table tbody tr.month-end-counterparty-row > td {
            border-top: 1px solid #eef0f3;
          }
          .month-end-outer-table tbody tr.month-end-counterparty-row-expanded {
            background: #eef4fb !important;
          }
          .month-end-outer-table tbody tr.month-end-counterparty-row-expanded > td {
            border-bottom: none;
          }
          /* Expansion row sits flush under the parent counterparty
             row — soft inset so the eye reads it as "inside" the
             counterparty, not as another peer row. */
          .month-end-expansion-row > td.month-end-expansion-cell {
            background: #f5f7fa !important;
            padding: 14px 16px !important;
            border-top: none !important;
            box-shadow: inset 0 2px 0 0 rgba(0, 0, 0, 0.04);
          }

          /* ---------- Inner per-booking table (inside expansion) ----- */
          .month-end-inner-table {
            background: white;
            border: 1px solid #e6e9ee;
            border-radius: 6px;
            overflow: hidden;
          }
          .month-end-inner-table thead th {
            background: #eef1f5 !important;
            color: #495057 !important;
            font-weight: 600 !important;
            font-size: 0.7rem !important;
            text-transform: uppercase !important;
            letter-spacing: 0.3px !important;
            border: none !important;
            border-bottom: 1px solid #dde2e8 !important;
            padding: 0.55rem 0.6rem !important;
          }
          .month-end-inner-table tbody tr.month-end-booking-row {
            cursor: pointer;
            background: white;
          }
          .month-end-inner-table tbody tr.month-end-booking-row:hover {
            background: #f6f8fb !important;
          }
          .month-end-inner-table tbody tr.month-end-booking-row > td {
            border-top: 1px solid #eef0f3 !important;
          }
          .month-end-inner-table tbody tr.month-end-booking-row-expanded,
          .month-end-inner-table tbody tr.month-end-booking-row-expanded:hover {
            background: #fafbfc !important;
          }
          /* Detail rows (order_line + payment entries) — softer still
             so the eye reads them as a third depth level. They are
             plain <tr> (no special class) inside the expanded summary. */
          .month-end-inner-table tbody tr:not(.month-end-booking-row) > td {
            background: #fcfcfd;
            border-top: 1px dashed #ecedf0 !important;
          }

          /* ---------- Filter chip of a record with no row this period ---------- */
          .rbt-token.month-end-filter-chip-inactive {
            background-color: #f1f3f5;
            color: #6c757d;
          }
        `}
      </style>

      <div className="card mt-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h2 className="mb-0">
              <i className="fa fa-calendar-check me-2 text-success" />
              Month End
            </h2>
            <div className="d-flex align-items-center gap-2">
              <Form.Group controlId="month_end.month" className="mb-0">
                <Form.Control
                  type="month"
                  value={month}
                  onChange={(e) => {
                    setMonth(e.target.value);
                    setFromOverride('');
                    setToOverride('');
                  }}
                />
              </Form.Group>
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowRangeRefiner((v) => !v)}
              >
                {showRangeRefiner ? 'Hide refiner' : 'Refine range'}
              </Button>
            </div>
          </div>

          {showRangeRefiner && (
            <div className="mt-3 p-3 border rounded bg-light">
              <div className="text-muted small mb-2">
                Override the auto-derived month boundaries. Leaving a
                field blank falls back to the start / end of the
                selected month.
              </div>
              <Form className="row gy-2">
                <Form.Group controlId="month_end.from" className="col-md-4 col-12">
                  <Form.Label className="small mb-1">From</Form.Label>
                  <Form.Control
                    type="date"
                    value={fromOverride}
                    onChange={(e) => setFromOverride(e.target.value)}
                  />
                </Form.Group>
                <Form.Group controlId="month_end.to" className="col-md-4 col-12">
                  <Form.Label className="small mb-1">To</Form.Label>
                  <Form.Control
                    type="date"
                    value={toOverride}
                    onChange={(e) => setToOverride(e.target.value)}
                  />
                </Form.Group>
                <div className="col-md-4 col-12 d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setFromOverride('');
                      setToOverride('');
                    }}
                  >
                    Reset to month
                  </Button>
                </div>
              </Form>
              <div className="text-muted small mt-2">
                {`Effective range: ${from} → ${to}`}
              </div>
            </div>
          )}

          {/* The counterparty filter — the screen's one search box. Kept at
              the top so a name in mind is found without scrolling. */}
          <div className="mt-4">
            <Form.Label className="fw-semibold" htmlFor="month-end-counterparty-input">
              Show only these counterparties
            </Form.Label>
            {summaryError && <Error error={summaryError} />}
            <Typeahead
              ref={filterRef}
              id="month-end-counterparty"
              multiple
              labelKey="label"
              options={menuOptions}
              selected={selectedOptions}
              filterBy={(option, { text }) => !!option.addAll || isOffered(option, text)}
              onInputChange={(text) => setFilterText(text)}
              onChange={onFilterChange}
              renderMenuItemChildren={renderFilterOption}
              renderToken={renderFilterChip}
              isLoading={summaryLoading}
              placeholder={summaryLoading
                ? 'Loading month-end summary…'
                : 'Type a name, a currency or "expense" — pick one or several'}
              inputProps={{ id: 'month-end-counterparty-input' }}
              minLength={0}
              maxResults={50}
            />
            <div className="text-muted small mt-1 d-flex flex-wrap align-items-center gap-2">
              {summaryLoading && 'Loading…'}
              {!summaryLoading && !summary && 'No summary loaded.'}
              {!summaryLoading && summary && !filterActive && (
                `${filterOptions.length} counterparties this period · type a name and choose "Add all matching" to see all of a driver's records at once`
              )}
              {!summaryLoading && summary && filterActive && (
                <span>
                  {`Showing ${visibleCount} of ${filterOptions.length} counterparties`}
                  {inactiveCount > 0 && ` · ${inactiveCount} selected with no activity in this period`}
                </span>
              )}
              {filterActive && (
                <Button variant="link" size="sm" className="p-0 align-baseline" onClick={clearFilter}>
                  Clear all
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sections — balance-sheet layout. Each section is its own
          Card with a roll-up header and a body of counterparty
          rows. With the counterparty filter on, each shows only the
          selected rows (the rest are hidden, never merged), and the
          selection's own totals sit above them. */}
      <div className="mt-3">
        {filterActive && (
          <SelectionTotals
            operatorSideRows={visibleOperatorSideRows}
            partnerRows={visiblePartnerRows}
            canSeeProfit={canSeeProfit}
          />
        )}

        {summaryLoading && !summary && (
          <Card className="mb-3">
            <Card.Body className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" />
              <span className="ms-2 text-muted">Loading month-end summary…</span>
            </Card.Body>
          </Card>
        )}

        {/* Three sections, not two. The first two are BOTH the operator
            side — same `side`, same row shape, same toggle factory, same
            detail fetch — split only by which heading they sit under, so no
            amount differs from the single Operators section this replaced.
            `Expenses` is the handful of own-fleet cost buckets; keeping them
            among the suppliers made "how many operators did we use" and
            "what do we owe suppliers" both wrong at a glance. */}
        {reportError && <Error error={reportError} />}

        <SectionAccordion
          title="Operators"
          icon="fa fa-truck"
          rows={visibleSupplierRows}
          totalCount={filterActive ? supplierRows.length : null}
          isOpen={operatorsOpen}
          onToggle={() => setOperatorsOpen((v) => !v)}
          expandedRowId={expandedOperatorId}
          toggleRow={toggleRowFactory('operator')}
          detailStateById={detailById}
          setDrilldownTrip={setDrilldownTrip}
          onBulkPay={setBulkPaymentTarget}
          onDownloadCsv={downloadCsv}
          onDownloadReport={downloadReport}
          onOpenLedger={setLedgerTarget}
          fxRates={summaryFxRates}
          canSeeProfit={canSeeProfit}
        />

        <SectionAccordion
          title="Expenses"
          icon="fa fa-receipt"
          rows={visibleExpenseRows}
          totalCount={filterActive ? expenseRows.length : null}
          isOpen={expensesOpen}
          onToggle={() => setExpensesOpen((v) => !v)}
          expandedRowId={expandedOperatorId}
          toggleRow={toggleRowFactory('operator')}
          detailStateById={detailById}
          setDrilldownTrip={setDrilldownTrip}
          onBulkPay={setBulkPaymentTarget}
          onDownloadCsv={downloadCsv}
          onDownloadReport={downloadReport}
          onOpenLedger={setLedgerTarget}
          fxRates={summaryFxRates}
          canSeeProfit={canSeeProfit}
        />

        <SectionAccordion
          title="Partners"
          icon="fa fa-handshake"
          rows={visiblePartnerRows}
          totalCount={filterActive ? partnerRows.length : null}
          isOpen={partnersOpen}
          onToggle={() => setPartnersOpen((v) => !v)}
          expandedRowId={expandedPartnerId}
          toggleRow={toggleRowFactory('partner')}
          detailStateById={detailById}
          setDrilldownTrip={setDrilldownTrip}
          onBulkPay={setBulkPaymentTarget}
          onDownloadCsv={downloadCsv}
          onOpenLedger={setLedgerTarget}
          fxRates={summaryFxRates}
          canSeeProfit={canSeeProfit}
        />
      </div>

      {/* Drill-down — one booking's Order Ledger, read-only: this page
          reads the ledger, it does not write it. */}
      {drilldownTrip && (
        <OrderDetailsModal
          order={drilldownTrip}
          show
          handleClose={() => setDrilldownTrip(null)}
          initialTab="ledger"
          ledgerReadOnly
        />
      )}

      {/* Bulk Payment modal — pre-filled from whichever counterparty row
          was clicked. Direction defaults to 'out' (operator settlement
          is the dominant case); user can flip for partner refunds. The
          period mirrors the current Month End range, and currency
          comes from the row's settlement / booking currency so the
          outstanding-lines fetch isn't silently filtered to EUR. */}
      <CounterpartyLedgerModal
        show={!!ledgerTarget}
        onHide={() => setLedgerTarget(null)}
        counterparty={
          ledgerTarget
            ? {
              kind: ledgerTarget.side,
              id: ledgerTarget.fetchId || ledgerTarget.id,
              name: ledgerTarget.name,
            }
            : null
        }
      />

      <BulkPaymentModal
        show={!!bulkPaymentTarget}
        onHide={() => setBulkPaymentTarget(null)}
        onRecorded={() => {
          setBulkPaymentTarget(null);
          // Invalidate caches so the row + drill-in reflect the new
          // payment immediately. `refresh: true` makes the summary
          // endpoint bypass its 5-min cache so we don't show stale
          // pre-payment numbers on the row.
          setDetailById(new Map());
          fetchSummary({ refresh: true });
        }}
        presetCounterparty={
          bulkPaymentTarget
            ? {
              kind: bulkPaymentTarget.side,
              id: bulkPaymentTarget.fetchId || bulkPaymentTarget.id,
              name: bulkPaymentTarget.name,
            }
            : undefined
        }
        presetPeriod={bulkPaymentTarget ? { from, to } : undefined}
        // Operator settlement = we pay them (out). Partner settlement =
        // partner pays us (in). Default to whichever makes sense for
        // the row's side; admins can still flip the direction inside
        // the modal for refund/recoupment flows.
        presetDirection={bulkPaymentTarget?.side === 'partner' ? 'in' : 'out'}
        presetCurrency={bulkPaymentTarget?.currency}
      />
    </>
  );
}

export default MonthEnd;

// Test-only named exports — the per-side row builders are otherwise
// module-private. Exposed so unit tests can exercise the summary-row
// renderer (including the original-currency secondary line under Gross)
// without bootstrapping the full MonthEnd page + API mocks. Not part
// of the public component contract; treat as internal.
export const __testables__ = {
  operatorSideRowsForTrip,
  partnerSideRowsForTrip,
  // Exposed so the Profit-column UI test can assert CSV header/body
  // composition without bootstrapping the whole MonthEnd page.
  buildCounterpartyCsv,
  normaliseOperatorRow,
  normalisePartnerRow,
  // The three-way grouping, exposed so a test can run the same summary
  // through it and compare the money before and after the split without
  // bootstrapping the whole page.
  partitionByOperatorType,
  counterpartyKindLabel,
  rollupByCurrency,
  // The counterparty filter's pure parts: what matches, what each option
  // searches, and which rows a selection shows.
  matchesEveryWord,
  filterOptionsFor,
  rowsInSelection,
};
